import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plane,
  Shield,
  FileText,
  Check,
  Upload,
  AlertCircle,
  QrCode,
  Share2,
  Calendar,
  Globe,
  Download,
  ChevronRight,
  Copy,
  Eye,
  Link,
  Printer,
  Edit,
  Plus,
  CheckCircle,
  Clock,
  XCircle,
  Activity,
  FileText,
  ShieldCheck,
  ExternalLink,
} from 'lucide-react';
import type { Pet } from '../../types/pet';
import { copyToClipboard } from '../../utils/clipboard';
import { ShareOptionsModal } from '../shared/ShareOptionsModal';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { DocumentViewModal } from './DocumentViewModal';
import { DocumentUploadModal } from './DocumentUploadModal';
import { EditFieldModal } from './EditFieldModal';
import { ReviewModal } from './ReviewModal';
import { QRCodeCanvas } from '../shared/QRCodeCanvas';
import { AddVaccinationModal } from '../records/AddVaccinationModal';
import { BookingFlow } from '../appointments/BookingFlow';
import { AddMedicalUpdateModal } from '../records/AddMedicalUpdateModal';
import { AddMetricModal } from '../records/AddMetricModal';
import { InsuranceRecords } from '../records/InsuranceRecords';
import { MicrochipLinking, MicrochipData } from '../microchip/MicrochipLinking';
import { MicrochipManagement } from '../microchip/MicrochipManagement';
import { apiClient } from '@/api/axios';
import { usePetPhoto } from '../../hooks/usePetPhoto';
import { usePets } from '../../context/PetsContext';
import { useNotifications } from '../../context/NotificationsContext';
import {
  useVaccinations,
  useMedicalUpdates,
  usePhysicalMetrics,
  useCreateVaccination,
  useCreateMedicalUpdate,
  useCreatePhysicalMetric,
} from '../../../../hooks/usePetRecords';
import {
  getPetById,
  linkMicrochip,
  updateMicrochipSettings,
  unlinkMicrochip,
  type MetricType,
  type UpdaterType,
} from '@/api/pets';

/** Safe date format for display; returns "—" if invalid or missing */
function formatDateSafe(value: string | undefined | null): string {
  if (value === undefined || value === null || String(value).trim() === '')
    return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

interface PassportDocument {
  name: string;
  type: string;
  date: string;
  size: string;
  source: string;
  url?: string;
}

interface PassportDocumentCardProps {
  document: PassportDocument;
  onView: (doc: PassportDocument) => void;
  onReplace: (doc: PassportDocument) => void;
  onDownload: (doc: PassportDocument) => void;
}

function PassportDocumentCard({
  document,
  onView,
  onReplace,
  onDownload,
}: PassportDocumentCardProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
                {document.name}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-400">
                {document.type} • {t('epassportModal.uploadedOn')}{' '}
                {formatDateSafe(document.date)}
              </div>
            </div>
            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs flex items-center gap-1">
              <Check className="w-3 h-3" />
              {t('epassport.uploaded')}
            </span>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => onView(document)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs flex items-center gap-1"
            >
              <Eye className="w-3 h-3" />
              {t('epassportModal.viewDocument')}
            </button>
            <button
              onClick={() => onReplace(document)}
              className="px-3 py-1.5 border border-blue-300 dark:border-blue-700/30 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs"
            >
              {t('epassportModal.replace')}
            </button>
            <button
              onClick={() => onDownload(document)}
              className="px-3 py-1.5 border border-blue-300 dark:border-blue-700/30 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs flex items-center gap-1"
            >
              <Download className="w-3 h-3" />
              {t('epassportModal.download')}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-blue-200 dark:border-blue-700/30">
            <div>
              <div className="text-xs text-blue-700 dark:text-blue-400">
                {t('epassportModal.uploadedByLabel')}
              </div>
              <div className="text-xs font-medium text-blue-900 dark:text-blue-300">
                {document.source}
              </div>
            </div>
            <div>
              <div className="text-xs text-blue-700 dark:text-blue-400">
                {t('epassportModal.fileType')}
              </div>
              <div className="text-xs font-medium text-blue-900 dark:text-blue-300">
                {document.type}
              </div>
            </div>
            <div>
              <div className="text-xs text-blue-700 dark:text-blue-400">
                {t('epassportModal.fileSize')}
              </div>
              <div className="text-xs font-medium text-blue-900 dark:text-blue-300">
                {document.size}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PetEPassportProps {
  pet: Pet;
}

interface EPassportSection {
  id: string;
  title: string;
  icon: string;
  completed: boolean;
  required: boolean;
  items?: number;
  total?: number;
}

interface CountryRequirement {
  code: string;
  name: string;
  region: string;
  requirements: {
    name: string;
    status: 'compliant' | 'action-required' | 'not-compliant';
    details: string;
  }[];
  notes: string[];
}

type EPassportStatus = 'not-created' | 'incomplete' | 'complete';
type ViewType =
  | 'main'
  | 'passport-view'
  | 'section-detail'
  | 'travel-check'
  | 'share'
  | 'qr-view';

function getTravelCheckRegionKey(region: string): string {
  const map: Record<string, string> = {
    Europe: 'europe',
    'North America': 'northAmerica',
    'South America': 'southAmerica',
    Asia: 'asia',
    'Asia-Pacific': 'asiaPacific',
  };
  return map[region] ?? region;
}

function getTravelCheckCountryName(
  t: (key: string) => string,
  code: string,
  name: string
): string {
  const key = `travelCheck.country.${code}`;
  const translated = t(key);
  return translated !== key ? translated : name;
}

const TRAVEL_CHECK_REQ_KEYS: Record<string, string> = {
  'Rabies Vaccination': 'rabiesVaccination',
  'Health Certificate (within 10 days)': 'healthCertificate10Days',
  'Parasite Treatment (24-120 hours)': 'parasiteTreatment',
  'Microchip ISO 11784/11785': 'microchipIso',
  'EU Pet Passport': 'euPetPassport',
  Microchip: 'microchip',
  'Health Certificate': 'healthCertificate',
  'Tapeworm Treatment': 'tapewormTreatment',
  'Import Permit': 'importPermit',
  'CDC Dog Import Form': 'cdcDogImportForm',
  'Parasite Treatment Certificate': 'parasiteTreatmentCertificate',
  'Rabies Vaccination (2 doses)': 'rabiesVaccination2Doses',
  'Rabies Antibody Test': 'rabiesAntibodyTest',
  'Import Notification': 'importNotification',
  Quarantine: 'quarantine',
  'Import License': 'importLicense',
  'Rabies FAVN Test': 'rabiesFavnTest',
  'Quarantine (minimum 10 days)': 'quarantineMin10Days',
  'Rabies Vaccination & Titer Test': 'rabiesVaccinationTiterTest',
  'Quarantine (10-180 days)': 'quarantine10_180Days',
};

const TRAVEL_CHECK_DETAIL_KEYS: Record<string, string> = {
  'Complete E-Passport sections to verify': 'completeSectionsToVerify',
  'Expired - Requires renewal': 'expiredRequiresRenewal',
  'Not completed': 'notCompleted',
  'Required for EU travel': 'requiredForEuTravel',
  Required: 'required',
  'Required within 10 days': 'requiredWithin10Days',
  'Valid for dogs': 'validForDogs',
  'Must complete online': 'mustCompleteOnline',
  'Valid certificate required': 'validCertificateRequired',
  Recommended: 'recommended',
  'Required within 5 days': 'requiredWithin5Days',
  '24-120 hours before entry': 'hoursBeforeEntry',
  'Required for dogs': 'requiredForDogs',
  'Apply in advance': 'applyInAdvance',
  'Apply via VIGIAGRO': 'applyViaVigiagro',
  'Government endorsed': 'governmentEndorsed',
  'SENASA permit required': 'senasaPermitRequired',
  'SAG approval required': 'sagApprovalRequired',
  'Endorsed by official vet': 'endorsedByOfficialVet',
  'ICA endorsed': 'icaEndorsed',
  'Valid certificate': 'validCertificate',
  'ISO 11784/11785': 'iso11784_11785',
  'With 30-day wait': 'with30DayWait',
  'Required 180 days before': 'required180DaysBefore',
  '40 days before arrival': 'daysBeforeArrival',
  'Required from authorities': 'requiredFromAuthorities',
  'Official endorsement required': 'officialEndorsementRequired',
  '7-30 days depending on city': 'daysDependingOnCity',
  'Apply to AVS': 'applyToAvs',
  'If not from approved country': 'ifNotFromApprovedCountry',
  'From Livestock Dept': 'fromLivestockDept',
  'Within 10 days': 'within10Days',
  'From DAFF': 'fromDaff',
  'Post Arrival Quarantine facility': 'postArrivalQuarantine',
  'With 180-day wait': 'with180DayWait',
  'From MPI': 'fromMpi',
  '180-day waiting period': '180DayWaitingPeriod',
  'Depends on country of origin': 'dependsOnCountryOfOrigin',
  'AQCS permit required': 'aqcsPermitRequired',
  'From Ministry of Climate Change': 'fromMinistryOfClimateChange',
  'ISO recommended': 'isoRecommended',
  'Requires veterinary endorsement': 'requiresVeterinaryEndorsement',
};

const TRAVEL_CHECK_NOTE_KEYS: Record<string, string> = {
  '21-day waiting period after rabies vaccination': 'waitingPeriod21Days',
  'Pet Passport or AHC required': 'petPassportOrAhc',
  'Tapeworm treatment required for dogs': 'tapewormForDogs',
  'Must be microchipped before vaccination': 'microchipBeforeVaccination',
  'Category 1 & 2 dogs require special permits': 'category1_2DogsPermit',
  'Dangerous breeds list applies': 'dangerousBreedsList',
  'Import permit may be required': 'importPermitMayBeRequired',
  'Pet passport required': 'petPassportRequired',
  'Register within 7 days of arrival': 'registerWithin7Days',
  'Potentially dangerous breeds require special license':
    'dangerousBreedsLicense',
  'Tapeworm treatment for dogs': 'tapewormTreatmentForDogs',
  'High-risk breeds prohibited': 'highRiskBreedsProhibited',
  'Declaration of no travel to rabies areas': 'noTravelToRabiesAreas',
  'Maximum 5 pets per person': 'max5PetsPerPerson',
  'Import permit for young animals': 'importPermitYoungAnimals',
  'Strict rabies regulations': 'strictRabiesRegulations',
  'Advance application required': 'advanceApplicationRequired',
  'Special rules for dangerous breeds': 'specialRulesDangerousBreeds',
  'Veterinary certificate required': 'veterinaryCertificateRequired',
  'Leishmaniasis prevention recommended': 'leishmaniasisRecommended',
  'Health certificate within 10 days': 'healthCertWithin10Days',
  'State-specific requirements may apply': 'stateSpecificRequirements',
  'CDC restrictions for high-risk countries': 'cdcRestrictions',
  'Cats generally do not require rabies cert': 'catsNoRabiesCert',
  'Young puppies have different rules': 'youngPuppiesDifferentRules',
  'Must be healthy on arrival': 'healthyOnArrival',
  'Maximum 3 months stay without import permit': 'max3MonthsStay',
  'Must enter through authorized ports': 'authorizedPortsOnly',
  'Must arrive within 60 days of health cert': 'within60DaysOfHealthCert',
  'Airport quarantine inspection required': 'airportQuarantineInspection',
  'Arrive within 10 days of certificate': 'arriveWithin10Days',
  'Additional vaccinations may be required':
    'additionalVaccinationsMayBeRequired',
  'Apply for permit 30 days in advance': 'apply30DaysInAdvance',
  'Quarantine may be required': 'quarantineMayBeRequired',
  'Certificate valid for 10 days': 'certificateValid10Days',
  'Inspection at airport required': 'inspectionAtAirport',
  'Very strict process - start 7+ months early': 'veryStrictProcess',
  'Must use approved labs for testing': 'approvedLabsForTesting',
  '180-day waiting period after blood test': '180DayWaitAfterBloodTest',
  'Breed restrictions apply': 'breedRestrictionsApply',
  'One pet per person limit': 'onePetPerPerson',
  'Major cities have specific rules': 'majorCitiesSpecificRules',
  '30-day quarantine for non-approved countries': 'quarantine30DaysNonApproved',
  'Apply for license 2 weeks before': 'applyLicense2WeeksBefore',
  'Apply for permit 15 days in advance': 'apply15DaysInAdvance',
  'Enter through approved airports only': 'approvedAirportsOnly',
  'Extremely strict - plan 6+ months ahead': 'extremelyStrict6Months',
  'Costly quarantine required': 'costlyQuarantineRequired',
  'Must use approved routes': 'mustUseApprovedRoutes',
  'Apply for permit 2+ months before': 'apply2MonthsBefore',
  'Approved countries have shorter quarantine':
    'approvedCountriesShorterQuarantine',
  'Very high standards required': 'veryHighStandards',
  'Apply for permit 60 days in advance': 'apply60DaysInAdvance',
  'Enter through specific airports only': 'specificAirportsOnly',
  'No quarantine for most countries': 'noQuarantineMostCountries',
  'Original health certificate required': 'originalHealthCertRequired',
  'Quarantine inspection at airport': 'quarantineInspectionAtAirport',
  'May require blood test from non-rabies-free countries':
    'bloodTestNonRabiesFree',
};

const countries: CountryRequirement[] = [
  // Europe
  {
    code: 'UK',
    name: 'United Kingdom',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Health Certificate (within 10 days)',
        status: 'not-compliant',
        details: 'Expired - Requires renewal',
      },
      {
        name: 'Parasite Treatment (24-120 hours)',
        status: 'action-required',
        details: 'Not completed',
      },
      {
        name: 'Microchip ISO 11784/11785',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      '21-day waiting period after rabies vaccination',
      'Pet Passport or AHC required',
      'Tapeworm treatment required for dogs',
    ],
  },

  {
    code: 'FR',
    name: 'France',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'EU Pet Passport',
        status: 'action-required',
        details: 'Required for EU travel',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      'Must be microchipped before vaccination',
      'Category 1 & 2 dogs require special permits',
    ],
  },

  {
    code: 'DE',
    name: 'Germany',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'EU Pet Passport',
        status: 'action-required',
        details: 'Required',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: ['Dangerous breeds list applies', 'Import permit may be required'],
  },

  {
    code: 'IT',
    name: 'Italy',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Health Certificate',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: ['Pet passport required', 'Register within 7 days of arrival'],
  },

  {
    code: 'ES',
    name: 'Spain',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'EU Pet Passport',
        status: 'action-required',
        details: 'Required',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      'Potentially dangerous breeds require special license',
      'Tapeworm treatment for dogs',
    ],
  },

  {
    code: 'NL',
    name: 'Netherlands',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'EU Pet Passport',
        status: 'action-required',
        details: 'Required',
      },
    ],
    notes: [
      'High-risk breeds prohibited',
      'Declaration of no travel to rabies areas',
    ],
  },

  {
    code: 'SE',
    name: 'Sweden',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Tapeworm Treatment',
        status: 'action-required',
        details: '24-120 hours before entry',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: ['Maximum 5 pets per person', 'Import permit for young animals'],
  },

  {
    code: 'NO',
    name: 'Norway',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Tapeworm Treatment',
        status: 'action-required',
        details: 'Required for dogs',
      },
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'Apply in advance',
      },
    ],
    notes: ['Strict rabies regulations', 'Advance application required'],
  },

  {
    code: 'CH',
    name: 'Switzerland',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Health Certificate',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      'Special rules for dangerous breeds',
      'Veterinary certificate required',
    ],
  },

  {
    code: 'GR',
    name: 'Greece',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'EU Pet Passport',
        status: 'action-required',
        details: 'Required',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      'Leishmaniasis prevention recommended',
      'Health certificate within 10 days',
    ],
  },

  {
    code: 'IE',
    name: 'Ireland',
    region: 'Europe',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Health Certificate (within 10 days)',
        status: 'not-compliant',
        details: 'Expired - Requires renewal',
      },
      {
        name: 'Parasite Treatment (24-120 hours)',
        status: 'action-required',
        details: 'Tapeworm treatment required for dogs',
      },
      {
        name: 'Microchip ISO 11784/11785',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      '21-day waiting period after rabies vaccination',
      'Pet Passport or AHC required',
      'Tapeworm treatment required for dogs',
    ],
  },

  // North America
  {
    code: 'US',
    name: 'United States',
    region: 'North America',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Valid for dogs',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Required within 10 days',
      },
      {
        name: 'CDC Dog Import Form',
        status: 'action-required',
        details: 'Must complete online',
      },
    ],
    notes: [
      'State-specific requirements may apply',
      'CDC restrictions for high-risk countries',
      'Cats generally do not require rabies cert',
    ],
  },

  {
    code: 'CA',
    name: 'Canada',
    region: 'North America',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Valid certificate required',
      },
      {
        name: 'Health Certificate',
        status: 'action-required',
        details: 'Recommended',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      'Young puppies have different rules',
      'Must be healthy on arrival',
      'Maximum 3 months stay without import permit',
    ],
  },

  {
    code: 'MX',
    name: 'Mexico',
    region: 'North America',
    requirements: [
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Required within 5 days',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Parasite Treatment Certificate',
        status: 'action-required',
        details: 'Recommended',
      },
    ],
    notes: [
      'Must enter through authorized ports',
      'Import permit may be required',
    ],
  },

  // South America
  {
    code: 'BR',
    name: 'Brazil',
    region: 'South America',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Valid certificate',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Requires veterinary endorsement',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'Apply via VIGIAGRO',
      },
    ],
    notes: [
      'Must arrive within 60 days of health cert',
      'Airport quarantine inspection required',
    ],
  },

  {
    code: 'AR',
    name: 'Argentina',
    region: 'South America',
    requirements: [
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Government endorsed',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'SENASA permit required',
      },
    ],
    notes: [
      'Arrive within 10 days of certificate',
      'Additional vaccinations may be required',
    ],
  },

  {
    code: 'CL',
    name: 'Chile',
    region: 'South America',
    requirements: [
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'SAG approval required',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Endorsed by official vet',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
    ],
    notes: [
      'Apply for permit 30 days in advance',
      'Quarantine may be required',
    ],
  },

  {
    code: 'CO',
    name: 'Colombia',
    region: 'South America',
    requirements: [
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'ICA endorsed',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Valid certificate',
      },
      { name: 'Microchip', status: 'action-required', details: 'Recommended' },
    ],
    notes: ['Certificate valid for 10 days', 'Inspection at airport required'],
  },

  // Asia
  {
    code: 'JP',
    name: 'Japan',
    region: 'Asia',
    requirements: [
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'ISO 11784/11785',
      },
      {
        name: 'Rabies Vaccination (2 doses)',
        status: 'action-required',
        details: 'With 30-day wait',
      },
      {
        name: 'Rabies Antibody Test',
        status: 'action-required',
        details: 'Required 180 days before',
      },
      {
        name: 'Import Notification',
        status: 'action-required',
        details: '40 days before arrival',
      },
    ],
    notes: [
      'Very strict process - start 7+ months early',
      'Must use approved labs for testing',
      '180-day waiting period after blood test',
    ],
  },

  {
    code: 'CN',
    name: 'China',
    region: 'Asia',
    requirements: [
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'Required from authorities',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Official endorsement required',
      },
      {
        name: 'Quarantine',
        status: 'action-required',
        details: '7-30 days depending on city',
      },
    ],
    notes: [
      'Breed restrictions apply',
      'One pet per person limit',
      'Major cities have specific rules',
    ],
  },

  {
    code: 'SG',
    name: 'Singapore',
    region: 'Asia',
    requirements: [
      {
        name: 'Import License',
        status: 'action-required',
        details: 'Apply to AVS',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Rabies FAVN Test',
        status: 'action-required',
        details: 'If not from approved country',
      },
    ],
    notes: [
      '30-day quarantine for non-approved countries',
      'Apply for license 2 weeks before',
    ],
  },

  {
    code: 'TH',
    name: 'Thailand',
    region: 'Asia',
    requirements: [
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'From Livestock Dept',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Within 10 days',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Valid certificate',
      },
    ],
    notes: [
      'Apply for permit 15 days in advance',
      'Enter through approved airports only',
    ],
  },

  {
    code: 'AU',
    name: 'Australia',
    region: 'Asia-Pacific',
    requirements: [
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'From DAFF',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'ISO 11784/11785',
      },
      {
        name: 'Rabies Vaccination & Titer Test',
        status: 'action-required',
        details: 'With 180-day wait',
      },
      {
        name: 'Quarantine (minimum 10 days)',
        status: 'action-required',
        details: 'Post Arrival Quarantine facility',
      },
    ],
    notes: [
      'Extremely strict - plan 6+ months ahead',
      'Costly quarantine required',
      'Must use approved routes',
    ],
  },

  {
    code: 'NZ',
    name: 'New Zealand',
    region: 'Asia-Pacific',
    requirements: [
      { name: 'Import Permit', status: 'action-required', details: 'From MPI' },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Rabies Vaccination & Titer Test',
        status: 'action-required',
        details: '180-day waiting period',
      },
      {
        name: 'Quarantine (10-180 days)',
        status: 'action-required',
        details: 'Depends on country of origin',
      },
    ],
    notes: [
      'Apply for permit 2+ months before',
      'Approved countries have shorter quarantine',
      'Very high standards required',
    ],
  },

  {
    code: 'IN',
    name: 'India',
    region: 'Asia',
    requirements: [
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'AQCS permit required',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Valid certificate',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Official endorsement required',
      },
    ],
    notes: [
      'Apply for permit 60 days in advance',
      'Enter through specific airports only',
    ],
  },

  {
    code: 'AE',
    name: 'United Arab Emirates',
    region: 'Asia',
    requirements: [
      {
        name: 'Import Permit',
        status: 'action-required',
        details: 'From Ministry of Climate Change',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Complete E-Passport sections to verify',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Within 10 days',
      },
    ],
    notes: [
      'No quarantine for most countries',
      'Breed restrictions apply',
      'Original health certificate required',
    ],
  },

  {
    code: 'KR',
    name: 'South Korea',
    region: 'Asia',
    requirements: [
      {
        name: 'Rabies Vaccination',
        status: 'action-required',
        details: 'Valid certificate',
      },
      {
        name: 'Health Certificate',
        status: 'not-compliant',
        details: 'Within 10 days',
      },
      {
        name: 'Microchip',
        status: 'action-required',
        details: 'ISO recommended',
      },
    ],
    notes: [
      'Quarantine inspection at airport',
      'May require blood test from non-rabies-free countries',
    ],
  },
];

export function PetEPassport({ pet }: PetEPassportProps) {
  const { t } = useTranslation();
  const { refreshPets } = usePets();
  const { addNotification } = useNotifications();
  const { data: vaccinations = [] } = useVaccinations(pet.id);
  const { data: medicalUpdates = [] } = useMedicalUpdates(pet.id);
  const { data: physicalMetrics = [] } = usePhysicalMetrics(pet.id);
  const [petCertificates, setPetCertificates] = useState<any[]>([]);
  const [viewingTravelCert, setViewingTravelCert] = useState<any>(null);
  const [showTravelBooking, setShowTravelBooking] = useState(false);

  useEffect(() => {
    if (pet?.id) {
      apiClient.get(`/pets/${pet.id}/certificates`).then(({ data }) => {
        setPetCertificates(data.certificates || []);
      }).catch(() => {});
    }
  }, [pet?.id]);
  const createVaccinationMutation = useCreateVaccination(pet.id);
  const createMedicalUpdateMutation = useCreateMedicalUpdate(pet.id);
  const createPhysicalMetricMutation = useCreatePhysicalMetric(pet.id);
  const { signedImageUrl } = usePetPhoto({
    petId: pet.id,
    photoKey: pet.photoKey,
    imageUrl: pet.imageUrl,
  });
  const [currentView, setCurrentView] = useState<ViewType>('main');
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [shareSettings, setShareSettings] = useState({
    recipient: '',
    scope: 'full',
    duration: '7d',
  });

  // Modal states
  const [documentViewModal, setDocumentViewModal] = useState<{
    isOpen: boolean;
    document: PassportDocument | null;
  }>({ isOpen: false, document: null });
  const [documentUploadModal, setDocumentUploadModal] = useState<{
    isOpen: boolean;
    documentName: string;
    isReplace: boolean;
    documentUrl?: string;
  }>({ isOpen: false, documentName: '', isReplace: false });
  const [editFieldModal, setEditFieldModal] = useState<{
    isOpen: boolean;
    field: {
      status?: string;
      label?: string;
      name?: string;
      value?: string;
    } | null;
    sectionId?: string;
  }>({ isOpen: false, field: null, sectionId: undefined });
  const [reviewModal, setReviewModal] = useState<{
    isOpen: boolean;
    item: { status?: string; name?: string } | null;
  }>({ isOpen: false, item: null });
  const [showAddVaccinationModal, setShowAddVaccinationModal] = useState(false);
  const [vaccinationInitialVaccine, setVaccinationInitialVaccine] =
    useState('');
  const [showAddMedicalUpdateModal, setShowAddMedicalUpdateModal] =
    useState(false);
  const [showAddMetricModal, setShowAddMetricModal] = useState(false);
  const [showMicrochipLinkingModal, setShowMicrochipLinkingModal] =
    useState(false);
  const [showMicrochipManagementModal, setShowMicrochipManagementModal] =
    useState(false);
  const [showShareOptionsModal, setShowShareOptionsModal] = useState(false);
  const [microchipData, setMicrochipData] = useState<
    (MicrochipData & { linkedDate: string }) | null
  >(null);
  const EPASSPORT_STORAGE_KEY = `petso_epassport_${pet.id}`;
  const PASSPORT_DOCS_KEY = `petso_passport_docs_${pet.id}`;

  const [passportDocuments, setPassportDocuments] = useState<
    PassportDocument[]
  >(() => {
    try {
      const saved = localStorage.getItem(`petso_passport_docs_${pet.id}`);
      return saved ? (JSON.parse(saved) as PassportDocument[]) : [];
    } catch {
      return [];
    }
  });
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [sectionDocuments, setSectionDocuments] = useState<
    Record<string, PassportDocument[]>
  >({});

  const loadOverrides = (): Record<string, string> => {
    try {
      const stored = localStorage.getItem(EPASSPORT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
      }
    } catch {
      // ignore
    }
    return {};
  };

  const [epassportOverrides, setEpassportOverridesState] =
    useState<Record<string, string>>(loadOverrides);

  const [ownerProfile, setOwnerProfile] = useState<{
    name: string;
    email: string;
    phone: string;
    address: {
      street?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      country?: string;
    };
  } | null>(null);

  useEffect(() => {
    setEpassportOverridesState(loadOverrides());
  }, [pet.id]);

  useEffect(() => {
    apiClient
      .get<{
        fullName?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        address?: {
          street?: string;
          city?: string;
          state?: string;
          zipCode?: string;
          country?: string;
        };
      }>('/users/profile')
      .then((res) => {
        const u = res.data;
        setOwnerProfile({
          name:
            u.fullName ||
            `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
            '',
          email: u.email || '',
          phone: u.phone || '',
          address: u.address || {},
        });
      })
      .catch(() => {
        // silently fall back to pet.owner data
      });
  }, []);

  // Fetch microchip data (shared with Overview - Manage Microchip)
  const loadMicrochip = useCallback(async () => {
    try {
      const { pet: fullPet } = await getPetById(pet.id);
      if (fullPet.microchip) {
        setMicrochipData({
          chipNumber: fullPet.microchip.chipNumber,
          standard: fullPet.microchip.standard,
          implantDate: fullPet.microchip.implantDate,
          implantLocation: fullPet.microchip.implantLocation,
          linkToDID: fullPet.microchip.linkToDID,
          visibility: fullPet.microchip.visibility,
          linkedDate: new Date().toISOString(),
        });
      } else {
        setMicrochipData(null);
      }
    } catch {
      setMicrochipData(null);
    }
  }, [pet.id]);

  useEffect(() => {
    loadMicrochip();
  }, [loadMicrochip]);

  // Refetch when Overview links/updates/unlinks microchip
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ petId: string }>;
      if (ev.detail?.petId === pet.id) loadMicrochip();
    };
    window.addEventListener('microchip-updated', handler);
    return () => window.removeEventListener('microchip-updated', handler);
  }, [pet.id, loadMicrochip]);

  const setEpassportOverrides = useCallback(
    (
      update:
        | Record<string, string>
        | ((prev: Record<string, string>) => Record<string, string>)
    ) => {
      setEpassportOverridesState((prev) => {
        const next = typeof update === 'function' ? update(prev) : update;
        try {
          localStorage.setItem(
            `petso_epassport_${pet.id}`,
            JSON.stringify(next)
          );
        } catch {
          // ignore
        }
        return next;
      });
    },
    [pet.id]
  );

  const validateRequirement = (reqName: string): { status: 'compliant' | 'action-required' | 'not-compliant'; proof?: any } => {
    const name = reqName.toLowerCase();

    // Rabies Vaccination
    if (name.includes('rabies') && name.includes('vaccin')) {
      const rabiesVax = vaccinations.find((v: any) =>
        v.vaccineName?.toLowerCase().includes('rabies') &&
        (!v.expiryDate || new Date(v.expiryDate) > new Date())
      );
      if (rabiesVax) return { status: 'compliant', proof: { type: 'vaccination', record: rabiesVax } };
      const rabiesCert = petCertificates.find((c: any) =>
        c.certificateType === 'rabies' && c.status === 'issued'
      );
      if (rabiesCert) return { status: 'compliant', proof: { type: 'certificate', record: rabiesCert } };
      return { status: vaccinations.length > 0 ? 'action-required' : 'not-compliant' };
    }

    // Health Certificate (within 10 days)
    if (name.includes('health certificate') || name.includes('health cert')) {
      const tenDaysAgo = new Date();
      tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
      const recentCert = petCertificates.find((c: any) =>
        (c.certificateType === 'comprehensive' || c.certificateType === 'travel-health') &&
        c.status === 'issued' &&
        new Date(c.issuedAt) >= tenDaysAgo
      );
      if (recentCert) return { status: 'compliant', proof: { type: 'certificate', record: recentCert } };
      const recentMedical = medicalUpdates.find((m: any) =>
        m.title?.toLowerCase().includes('health') &&
        m.verified &&
        new Date(m.date) >= tenDaysAgo
      );
      if (recentMedical) return { status: 'compliant', proof: { type: 'medical', record: recentMedical } };
      return { status: 'action-required' };
    }

    // Microchip
    if (name.includes('microchip')) {
      if (pet.microchipId || (pet as any).microchip?.chipNumber) {
        return { status: 'compliant', proof: { type: 'microchip' } };
      }
      return { status: 'not-compliant' };
    }

    // Parasite / Tapeworm Treatment
    if (name.includes('parasite') || name.includes('tapeworm')) {
      const dewormingCert = petCertificates.find((c: any) =>
        c.certificateType === 'deworming' && c.status === 'issued'
      );
      if (dewormingCert) return { status: 'compliant', proof: { type: 'certificate', record: dewormingCert } };
      const parasiteMed = medicalUpdates.find((m: any) =>
        (m.title?.toLowerCase().includes('parasite') || m.title?.toLowerCase().includes('tapeworm') || m.title?.toLowerCase().includes('deworming')) && m.verified
      );
      if (parasiteMed) return { status: 'compliant', proof: { type: 'medical', record: parasiteMed } };
      return { status: 'action-required' };
    }

    // EU Pet Passport
    if (name.includes('eu pet passport') || name.includes('pet passport')) {
      const hasRabies = vaccinations.some((v: any) => v.vaccineName?.toLowerCase().includes('rabies'));
      const hasMicrochip = !!(pet.microchipId || (pet as any).microchip?.chipNumber);
      if (hasRabies && hasMicrochip) return { status: 'compliant' };
      return { status: 'action-required' };
    }

    // Import Permit / License / CDC Form / Quarantine — administrative, can't auto-validate
    if (name.includes('import permit') || name.includes('import license') || name.includes('cdc') || name.includes('quarantine') || name.includes('import notification')) {
      return { status: 'action-required' };
    }

    // Rabies Antibody Test / FAVN Test / Titer Test
    if (name.includes('antibody') || name.includes('favn') || name.includes('titer')) {
      const titerCert = petCertificates.find((c: any) =>
        c.data && (typeof c.data === 'string' ? c.data : JSON.stringify(c.data)).toLowerCase().includes('titer')
      );
      if (titerCert) return { status: 'compliant', proof: { type: 'certificate', record: titerCert } };
      return { status: 'action-required' };
    }

    // General vaccination check
    if (name.includes('vaccin')) {
      const hasVax = vaccinations.length > 0;
      if (hasVax) return { status: 'compliant', proof: { type: 'vaccination', record: vaccinations[0] } };
      return { status: 'action-required' };
    }

    // Default: can't validate automatically
    return { status: 'action-required' };
  };

  const fetchSectionDocuments = async (folder: string) => {
    try {
      setIsLoadingDocuments(true);
      const res = await apiClient.get<{
        items: {
          key?: string | null;
          size?: number | null;
          lastModified?: string | Date | null;
        }[];
      }>('/documents', { params: { folder } });

      const items = res.data.items || [];

      const mapped: PassportDocument[] = items
        .filter((i) => i.key)
        .map((i) => {
          const key = i.key as string;
          const parts = key.split('/');
          const lastPart = parts[parts.length - 1] || '';
          const originalNameIdx = lastPart.indexOf('_');
          const originalName =
            originalNameIdx >= 0
              ? lastPart.substring(originalNameIdx + 1)
              : lastPart;

          const sizeInMB = i.size
            ? `${(i.size / (1024 * 1024)).toFixed(1)} MB`
            : '0 MB';

          return {
            name: originalName,
            type: lastPart.split('.').pop()?.toUpperCase() || 'FILE',
            date: (i.lastModified
              ? new Date(i.lastModified as string | Date)
              : new Date()
            )
              .toISOString()
              .split('T')[0],
            size: sizeInMB,
            source: 'Owner',
            url: key,
          };
        });

      setSectionDocuments((prev) => ({ ...prev, [folder]: mapped }));
      if (folder === 'passport') {
        // Only update + persist if the API returned items; otherwise keep the cache
        if (mapped.length > 0) {
          setPassportDocuments(mapped);
          try {
            localStorage.setItem(PASSPORT_DOCS_KEY, JSON.stringify(mapped));
          } catch {
            // localStorage full or unavailable — ignore
          }
        }
        // If mapped is empty, leave passportDocuments as-is (shows cached docs)
      }
    } catch (err) {
      console.error(`Failed to fetch documents for folder: ${folder}`, err);
      // On fetch error: keep existing cached passport documents rather than clearing them
      if (folder !== 'passport') {
        setSectionDocuments((prev) => ({ ...prev, [folder]: [] }));
      }
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const fetchPassportDocuments = () => fetchSectionDocuments('passport');

  useEffect(() => {
    // Fetch whenever selectedSection changes — including back to null (View Passport)
    if (selectedSection) {
      fetchSectionDocuments(selectedSection);
    } else {
      fetchPassportDocuments();
    }
  }, [selectedSection]);

  // Refetch microchip from API when user opens Microchip Information section,
  // so E-Passport always shows latest data (e.g. after updating in Overview → Manage Microchip)
  useEffect(() => {
    if (selectedSection === 'microchip') {
      loadMicrochip();
    }
  }, [selectedSection, loadMicrochip]);

  const sections: EPassportSection[] = useMemo(() => {
    const getSectionStats = (
      sectionId: string
    ): { items: number; total: number } => {
      const hasValue = (v: unknown) =>
        v !== undefined && v !== null && String(v).trim() !== '';

      switch (sectionId) {
        case 'identification': {
          const fields = [
            hasValue(pet.name),
            hasValue(pet.species),
            hasValue(pet.breed),
            hasValue(pet.dateOfBirth),
            hasValue(pet.gender),
            hasValue(pet.color),
          ];
          const completed = fields.filter(Boolean).length;
          return { items: completed, total: 6 };
        }
        case 'owner': {
          const addrCheck =
            [
              ownerProfile?.address?.city,
              ownerProfile?.address?.customCity,
              ownerProfile?.address?.country,
              ownerProfile?.address?.customCountry,
              ownerProfile?.address?.street,
            ].some(Boolean) || [pet.city, pet.country].some(Boolean);
          const fields = [
            hasValue(ownerProfile?.name) ||
              hasValue(pet.owner?.name) ||
              hasValue(epassportOverrides.owner_name),
            hasValue(ownerProfile?.email) ||
              hasValue(pet.owner?.email) ||
              hasValue(epassportOverrides.owner_contact_email),
            hasValue(ownerProfile?.phone) ||
              hasValue(epassportOverrides.owner_contact_phone),
            addrCheck,
          ];
          const completed = fields.filter(Boolean).length;
          return { items: completed, total: 4 };
        }
        case 'microchip': {
          const chipNum = microchipData?.chipNumber || pet.microchipId;
          const fields = [
            hasValue(chipNum),
            hasValue(microchipData?.standard),
            hasValue(microchipData?.implantDate),
            hasValue(microchipData?.implantLocation),
          ];
          const completed = fields.filter(Boolean).length;
          return { items: completed, total: 4 };
        }
        case 'vaccinations':
          return {
            items: vaccinations.length,
            total: vaccinations.length > 0 ? vaccinations.length : 1,
          };
        case 'health-certificates':
          return {
            items: medicalUpdates.length,
            total: medicalUpdates.length > 0 ? medicalUpdates.length : 1,
          };
        case 'travel':
          return { items: 0, total: 0 };
        default:
          return { items: 0, total: 0 };
      }
    };

    const configs: Array<{
      id: string;
      title: string;
      icon: string;
      required: boolean;
    }> = [
      {
        id: 'identification',
        title: t('epassport.petIdentification'),
        icon: '🐾',
        required: true,
      },
      {
        id: 'owner',
        title: t('epassport.ownerInformation'),
        icon: '👤',
        required: true,
      },
      {
        id: 'microchip',
        title: t('epassport.microchipInformation'),
        icon: '🔬',
        required: false,
      },
      {
        id: 'vaccinations',
        title: t('epassport.vaccinationRecords'),
        icon: '💉',
        required: true,
      },
      {
        id: 'health-certificates',
        title: t('epassport.healthCertificatesTests'),
        icon: '🏥',
        required: true,
      },
      {
        id: 'travel',
        title: t('epassport.travelBorderRequirements'),
        icon: '✈️',
        required: false,
      },
    ];
    return configs.map((config) => {
      const { items, total } = getSectionStats(config.id);
      return {
        ...config,
        items,
        total,
        completed: total > 0 && items === total,
      };
    });
  }, [
    t,
    pet,
    microchipData,
    sectionDocuments,
    epassportOverrides,
    ownerProfile,
    vaccinations,
    medicalUpdates,
    physicalMetrics,
  ]);

  // Exclude optional "travel" section from completion tracking — it's not mandatory
  const coreSections = sections.filter((s) => s.id !== 'travel');
  const completedSections = coreSections.filter((s) => s.completed).length;
  const totalSections = coreSections.length; // 5 (without travel)
  const requiredCompleted = sections.filter(
    (s) => s.required && s.completed
  ).length;
  const totalRequired = sections.filter((s) => s.required).length;
  const completionPercentage = Math.round(
    (completedSections / totalSections) * 100
  );
  const status: EPassportStatus =
    completedSections === totalSections
      ? 'complete'
      : completedSections > 0
        ? 'incomplete'
        : 'not-created';
  const qrContent = shareLink || `https://petso.io/share/epassport/${pet.id}`;

  // Generate Share Link
  const handleGenerateShareLink = () => {
    const linkId = Math.random().toString(36).substring(2, 15);
    const link = `https://petso.io/share/epassport/${pet.id}/${linkId}`;
    setShareLink(link);
  };

  // Copy Share Link
  const handleCopyShareLink = () => {
    copyToClipboard(shareLink);
  };

  // Download QR Code
  const handleDownloadQR = async () => {
    try {
      const dataUrl = await QRCode.toDataURL(qrContent, {
        width: 1024,
        margin: 2,
        color: { dark: '#2D2F7E', light: '#FFFFFF' },
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${pet.name}-epassport-qr.png`;
      a.click();
    } catch (error) {
      console.error('Failed to generate QR download:', error);
      toast.error('Failed to download QR. Please try again.');
    }
  };

  // Print QR Code
  const handlePrintQR = async () => {
    try {
      const dataUrl = await QRCode.toDataURL(qrContent, {
        width: 800,
        margin: 2,
        color: { dark: '#2D2F7E', light: '#FFFFFF' },
      });

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
        <html>
          <head>
            <title>${pet.name} E-Passport QR Code</title>
            <style>
              body { font-family: Arial; text-align: center; padding: 50px; }
              h1 { color: #2D2F7E; }
              .qr-box { border: 2px solid #2D2F7E; padding: 20px; margin: 20px auto; width: 420px; }
              .qr-img { width: 360px; height: 360px; }
            </style>
          </head>
          <body>
            <h1>${pet.name}'s E-Passport QR Code</h1>
            <div class="qr-box">
              <img class="qr-img" src="${dataUrl}" alt="E-Passport QR Code" />
              <p>DID: ${pet.did ?? '—'}</p>
              <p>Pet ID: ${pet.id}</p>
            </div>
            <script>window.print();</script>
          </body>
        </html>
      `);
        printWindow.document.close();
      }
    } catch (error) {
      console.error('Failed to generate QR for printing:', error);
      toast.error('Failed to print QR. Please try again.');
    }
  };

  // Export PDF
  const handleExportPDF = () => {
    const pdfContent = `
      PETSO E-PASSPORT
      ================
      
      Pet Name: ${pet.name}
      Species: ${pet.species}
      Breed: ${pet.breed}
      Date of Birth: ${formatDateSafe(pet.dateOfBirth)}
      Microchip: ${pet.microchipId ?? '—'}
      DID: ${pet.did ?? '—'}
      
      Owner: ${pet.owner?.name ?? '—'}
      Owner DID: ${pet.owner?.did ?? '—'}
      
      Status: ${status.toUpperCase()}
      Completion: ${completionPercentage}%
      
      Sections:
      ${sections.map((s) => `- ${s.icon} ${s.title}: ${s.completed ? 'Complete' : 'Incomplete'}`).join('\n')}
      
      Generated: ${new Date().toLocaleString()}
      
      This is a digital travel document. Not a replacement for official government documents.
    `;

    const blob = new Blob([pdfContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pet.name}-epassport.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle Document Download
  const handleDownload = async (d: PassportDocument) => {
    try {
      if (!d.url) {
        toast.error('No file URL available');
        return;
      }
      toast.success(`Downloading ${d.name}...`);
      let blobUrl: string;
      if (d.url.startsWith('http://') || d.url.startsWith('https://')) {
        const res = await fetch(d.url);
        if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
      } else {
        const encodedKey = encodeURIComponent(d.url);
        const res = await apiClient.get<Blob>(
          `/documents/${encodedKey}/download`,
          { params: { disposition: 'attachment' }, responseType: 'blob' }
        );
        blobUrl = URL.createObjectURL(res.data);
      }
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = d.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('Download failed', err);
      toast.error('Failed to download document');
    }
  };

  // Render content based on view
  const renderContent = () => {
    // Digital Passport View
    if (currentView === 'passport-view') {
      return (
        <div className="max-w-4xl mx-auto min-w-0 px-1 sm:px-0">
          <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              onClick={() => setCurrentView('main')}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              {t('epassport.backToDashboard')}
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 dark:text-white"
              >
                <Download className="w-4 h-4" />
                {t('epassport.exportButton')}
              </button>
              <button
                onClick={handlePrintQR}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-white/8 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 dark:text-white"
              >
                <Printer className="w-4 h-4" />
                {t('epassport.printButton')}
              </button>
            </div>
          </div>

          {/* Digital Passport Card - responsive padding, long values wrap on mobile */}
          <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-4 sm:p-8 text-white mb-6 min-w-0 overflow-hidden">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6">
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <Plane className="w-8 h-8 flex-shrink-0" />
                  <h1 className="text-2xl sm:text-3xl">
                    {t('epassport.viewPassportTitle')}
                  </h1>
                </div>
                <p className="text-sm opacity-90">
                  {t('epassport.digitalTravelIdDocument')}
                </p>
              </div>
              <div className="text-left sm:text-right flex-shrink-0">
                <div className="text-xs opacity-75">
                  {t('epassport.statusLabel')}
                </div>
                <div className="text-lg">
                  {status === 'complete' &&
                    `🟢 ${t('epassport.viewPassportStatusComplete')}`}
                  {status === 'incomplete' &&
                    `🟡 ${t('epassport.viewPassportStatusIncomplete')}`}
                  {status === 'not-created' &&
                    `❌ ${t('epassport.viewPassportStatusNotCreated')}`}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 min-w-0">
              <div className="min-w-0">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
                  <div className="w-32 h-32 bg-white/20 rounded-lg flex items-center justify-center text-6xl mx-auto mb-3 overflow-hidden">
                    {signedImageUrl ? (
                      <img
                        src={signedImageUrl}
                        alt={pet.name}
                        className="w-full h-full object-cover"
                      />
                    ) : pet.species === 'dog' ? (
                      '🐕'
                    ) : (
                      '🐈'
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-1">{pet.name ?? '—'}</div>
                    <div className="text-sm opacity-80">{pet.breed ?? '—'}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 min-w-0">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-0">
                  <div className="text-xs opacity-75 mb-1">
                    {t('epassport.fieldSpecies')}
                  </div>
                  <div className="capitalize">{pet.species ?? '—'}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <div className="text-xs opacity-75 mb-1">
                    {t('epassport.fieldDateOfBirth')}
                  </div>
                  <div>{formatDateSafe(pet.dateOfBirth)}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <div className="text-xs opacity-75 mb-1">
                    {t('epassport.fieldGender')}
                  </div>
                  <div className="capitalize">{pet.gender ?? '—'}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <div className="text-xs opacity-75 mb-1">
                    {t('epassport.fieldColor')}
                  </div>
                  <div>{pet.color ?? '—'}</div>
                </div>
              </div>
            </div>

            <div className="mt-4 sm:mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-0 overflow-hidden">
                <div className="text-xs opacity-75 mb-1">
                  {t('epassport.fieldMicrochipId')}
                </div>
                <div className="font-mono text-sm break-all">
                  {pet.microchipId ?? '—'}
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-0 overflow-hidden">
                <div className="text-xs opacity-75 mb-1">
                  {t('epassport.fieldDid')}
                </div>
                <div className="font-mono text-xs break-all">
                  {pet.did ?? '—'}
                </div>
              </div>
            </div>

            <div className="mt-4 sm:mt-6 bg-white/10 backdrop-blur-sm rounded-lg p-4 min-w-0 overflow-hidden">
              <div className="text-xs opacity-75 mb-2">
                {t('epassport.ownerInformation')}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
                <div className="min-w-0">
                  <div className="text-sm opacity-75">
                    {t('epassport.fieldOwnerName')}
                  </div>
                  <div>
                    {ownerProfile?.name ||
                      pet.owner?.name ||
                      epassportOverrides.owner_name ||
                      '—'}
                  </div>
                </div>
                <div className="min-w-0 overflow-hidden">
                  <div className="text-sm opacity-75">
                    {t('epassport.fieldOwnerDid')}
                  </div>
                  <div className="font-mono text-xs break-all">
                    {pet.owner?.did ?? '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Passport Section */}
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 mb-6 border dark:border-white/8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl dark:text-white flex items-center gap-2">
                <Upload className="w-5 h-5" />
                {t('epassport.uploadPassportDocument')}
              </h3>
            </div>

            {isLoadingDocuments ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : passportDocuments.length > 0 ? (
              <div className="space-y-4">
                {passportDocuments.map((doc, index) => (
                  <PassportDocumentCard
                    key={index}
                    document={doc}
                    onView={(d) =>
                      setDocumentViewModal({
                        isOpen: true,
                        document: d,
                      })
                    }
                    onReplace={(doc) =>
                      setDocumentUploadModal({
                        isOpen: true,
                        documentName: doc.name,
                        isReplace: true,
                        documentUrl: doc.url,
                      })
                    }
                    onDownload={handleDownload}
                  />
                ))}
                <button
                  onClick={() =>
                    setDocumentUploadModal({
                      isOpen: true,
                      documentName: t(
                        'epassport.additionalPassportDocumentLabel'
                      ),
                      isReplace: false,
                    })
                  }
                  className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg text-sm text-gray-600 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t('epassport.addAnotherDocument')}
                </button>
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg">
                <Upload className="w-12 h-12 text-gray-400 dark:text-white/40 mx-auto mb-3" />
                <p className="text-sm text-gray-600 dark:text-white/70 mb-4">
                  {t('epassport.noPassportDocumentUploadedYet')}
                </p>
                <button
                  onClick={() =>
                    setDocumentUploadModal({
                      isOpen: true,
                      documentName: t('epassport.passportDocumentLabel'),
                      isReplace: false,
                    })
                  }
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
                >
                  <Upload className="w-4 h-4" />
                  {t('epassport.uploadPassport')}
                </button>
              </div>
            )}
          </div>

          {/* Compliance Summary */}
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 mb-6 border dark:border-white/8">
            <h3 className="text-xl mb-4 dark:text-white">
              {t('epassport.travelComplianceSummary')}
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 rounded-lg p-4">
                <div className="text-2xl text-green-600 dark:text-green-400 mb-1">
                  ✓
                </div>
                <div className="text-lg text-green-900 dark:text-green-300">
                  {t('epassport.compliant')}
                </div>
                <div className="text-sm text-gray-600 dark:text-white/70">
                  {t('epassport.noDestinationsCheckedYet')}
                </div>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/30 rounded-lg p-4">
                <div className="text-2xl text-yellow-600 dark:text-yellow-400 mb-1">
                  ⚠
                </div>
                <div className="text-lg text-yellow-900 dark:text-yellow-300">
                  {t('epassport.actionRequired')}
                </div>
                <div className="text-sm text-gray-600 dark:text-white/70">
                  {t('epassport.useTravelCheckToAddDestinations')}
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-lg p-4">
                <div className="text-2xl text-red-600 dark:text-red-400 mb-1">
                  ✗
                </div>
                <div className="text-lg text-red-900 dark:text-red-300">
                  {t('epassport.notCompliant')}
                </div>
                <div className="text-sm text-gray-600 dark:text-white/70">
                  {t('epassport.completeRequirementsFirst')}
                </div>
              </div>
            </div>
          </div>

          {/* Section Completion */}
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 border dark:border-white/8">
            <h3 className="text-xl mb-4 dark:text-white">
              {t('epassport.documentSections')}
            </h3>
            <div className="space-y-3">
              {sections.map((section) => (
                <div
                  key={section.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#0F0E16] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{section.icon}</span>
                    <div>
                      <div className="dark:text-white">{section.title}</div>
                      {section.items !== undefined &&
                        section.total !== undefined && (
                          <div className="text-xs text-gray-500 dark:text-white/50">
                            {t('epassport.sectionItemsCount', {
                              items: section.items,
                              total: section.total,
                            })}
                          </div>
                        )}
                    </div>
                  </div>
                  {section.completed ? (
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4 text-center text-sm text-gray-600 dark:text-white/70">
            <Shield className="w-5 h-5 inline mr-2" />
            {t('epassport.passportSecuredBlockchain', { name: pet.name })}
            <br />
            {t('epassport.generated')} {new Date().toLocaleString()}
          </div>
        </div>
      );
    }

    // Main Dashboard View
    if (currentView === 'main') {
      return (
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-3xl shadow-lg">
                ✈️
              </div>
              <div>
                <h1 className="text-3xl dark:text-white">
                  {t('epassport.pageTitle')}
                </h1>
                <p className="text-gray-600 dark:text-white/70">
                  {t('epassport.pageSubtitle', { name: pet.name })}
                </p>
              </div>
            </div>

            {/* Status Bar */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {status === 'complete' && (
                    <span className="text-2xl">🟢</span>
                  )}
                  {status === 'incomplete' && (
                    <span className="text-2xl">🟡</span>
                  )}
                  {status === 'not-created' && (
                    <span className="text-2xl">❌</span>
                  )}
                  <span className="text-xl">
                    {status === 'complete' && t('epassport.status.complete')}
                    {status === 'incomplete' &&
                      t('epassport.status.incomplete')}
                    {status === 'not-created' &&
                      t('epassport.status.notCreated')}
                  </span>
                </div>
                <span className="text-xl">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3">
                <div
                  className="bg-white rounded-full h-3 transition-all duration-500"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 text-sm opacity-90">
                <span>
                  {t('epassport.sectionsComplete', {
                    completed: completedSections,
                    total: totalSections,
                  })}
                </span>
                <span>
                  {t('epassport.requiredSections', {
                    required: requiredCompleted,
                    total: totalRequired,
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <button
              onClick={() => setCurrentView('passport-view')}
              className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-2 border-blue-300 dark:border-blue-700/30 rounded-lg p-4 hover:shadow-md transition-all"
            >
              <Eye className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2 mx-auto" />
              <div className="text-sm text-blue-900 dark:text-blue-300">
                {t('epassport.viewPassport')}
              </div>
            </button>
            <button
              onClick={() => setCurrentView('travel-check')}
              className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-2 border-green-300 dark:border-green-700/30 rounded-lg p-4 hover:shadow-md transition-all"
            >
              <Globe className="w-8 h-8 text-green-600 dark:text-green-400 mb-2 mx-auto" />
              <div className="text-sm text-green-900 dark:text-green-300">
                {t('epassport.travelCheck')}
              </div>
            </button>
            <button
              onClick={() => setCurrentView('share')}
              className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-2 border-purple-300 dark:border-purple-700/30 rounded-lg p-4 hover:shadow-md transition-all"
            >
              <Share2 className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-2 mx-auto" />
              <div className="text-sm text-purple-900 dark:text-purple-300">
                {t('epassport.share')}
              </div>
            </button>
            <button
              onClick={() => setCurrentView('qr-view')}
              className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-2 border-indigo-300 dark:border-indigo-700/30 rounded-lg p-4 hover:shadow-md transition-all"
            >
              <QrCode className="w-8 h-8 text-indigo-600 dark:text-indigo-400 mb-2 mx-auto" />
              <div className="text-sm text-indigo-900 dark:text-indigo-300">
                {t('epassport.qrCode')}
              </div>
            </button>
            <button
              onClick={handleExportPDF}
              className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-2 border-orange-300 dark:border-orange-700/30 rounded-lg p-4 hover:shadow-md transition-all"
            >
              <Download className="w-8 h-8 text-orange-600 dark:text-orange-400 mb-2 mx-auto" />
              <div className="text-sm text-orange-900 dark:text-orange-300">
                {t('epassport.exportPdf')}
              </div>
            </button>
          </div>

          {/* Disclaimer */}
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-orange-800 dark:text-orange-300">
                {t('epassport.importantDisclaimer')}
              </div>
            </div>
          </div>

          {/* E-Passport Sections - mobile: stack layout, prevent button/card overflow */}
          <div className="mb-8 min-w-0">
            <h2 className="text-xl sm:text-2xl mb-4 dark:text-white">
              {t('epassport.sectionsTitle')}
            </h2>
            <div className="space-y-3">
              {/* Upload Passport Document Section */}
              <div
                className={`border-2 rounded-lg p-4 sm:p-5 hover:shadow-md transition-all min-w-0 ${
                  passportDocuments.length > 0
                    ? 'border-blue-300 dark:border-blue-700/30 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                    : 'border-gray-300 dark:border-white/8 bg-white dark:bg-[#16151D] hover:border-blue-300 dark:hover:border-blue-700/30'
                }`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="text-3xl sm:text-4xl flex-shrink-0">📄</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="text-base sm:text-lg dark:text-white">
                          {t('epassport.uploadPassportDocument')}
                        </h3>
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                          {t('epassport.recommended')}
                        </span>
                        {passportDocuments.length > 0 ? (
                          <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                            <Check className="w-4 h-4" />
                            {t('epassport.uploaded')}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-500 dark:text-white/50 text-sm">
                            {t('epassport.notUploaded')}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-white/70">
                        {passportDocuments.length > 0
                          ? t('epassport.fullPassportDocumentCount', {
                              count: passportDocuments.length,
                            })
                          : t('epassport.fullPassportDocumentNoDocs')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentView('passport-view');
                    }}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex-shrink-0"
                  >
                    <Eye className="w-4 h-4" />
                    {t('epassport.viewReplace')}
                  </button>
                </div>
              </div>

              {sections.map((section) => (
                <div
                  key={section.id}
                  className={`border-2 rounded-lg p-4 sm:p-5 transition-all cursor-pointer min-w-0 overflow-hidden active:scale-[0.99] ${
                    section.completed
                      ? 'border-green-300 dark:border-green-700/30 bg-green-50 dark:bg-green-900/20 hover:shadow-md'
                      : 'border-gray-300 dark:border-white/8 bg-white dark:bg-[#16151D] hover:border-blue-300 dark:hover:border-blue-700/30 hover:shadow-md'
                  }`}
                  onClick={() => {
                    setSelectedSection(section.id);
                    setCurrentView('section-detail');
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className="text-2xl sm:text-4xl flex-shrink-0">
                        {section.icon}
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                          <h3 className="text-base sm:text-lg font-medium dark:text-white">
                            {section.title}
                          </h3>
                          {section.required && (
                            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs whitespace-nowrap">
                              {t('epassport.required')}
                            </span>
                          )}
                          {section.completed && (
                            <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs sm:text-sm whitespace-nowrap">
                              <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              {t('epassport.complete')}
                            </span>
                          )}
                        </div>
                        {section.id === 'travel' ? (
                          <div className="text-sm text-gray-600 dark:text-white/70 break-words">
                            {t('epassport.selectDestinationToCheck')}
                          </div>
                        ) : (
                          section.items !== undefined &&
                          section.total !== undefined && (
                            <div className="text-sm text-gray-600 dark:text-white/70 break-words">
                              {section.id === 'vaccinations'
                                ? t('epassport.vaccinationsRecorded', {
                                    count: section.items,
                                  })
                                : section.id === 'health-certificates'
                                  ? t('epassport.medicalUpdatesRecorded', {
                                      count: section.items,
                                    })
                                  : t('epassport.itemsCompleted', {
                                      items: section.items,
                                      total: section.total,
                                    })}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 dark:text-white/50 flex-shrink-0 ml-0.5" />
                  </div>

                  {section.total !== undefined && section.total > 0 && (
                    <div className="mt-3 min-w-0">
                      <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2 min-w-0">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            section.completed
                              ? 'bg-green-500 dark:bg-green-400'
                              : 'bg-blue-500 dark:bg-blue-400'
                          }`}
                          style={{
                            width: `${((section.items || 0) / section.total) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Expiry Alerts */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-yellow-900 dark:text-yellow-300 mb-2">
                  {t('epassport.upcomingExpiries')}
                </h3>
                <div className="text-sm text-yellow-800 dark:text-yellow-300">
                  {t('epassport.upcomingExpiriesDesc')}
                </div>
              </div>
            </div>
          </div>

          {/* Security Info */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-700/30 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5" />
              <div>
                <h3 className="text-indigo-900 dark:text-indigo-300 mb-2">
                  {t('epassport.privacySecurity')}
                </h3>
                <ul className="text-sm text-indigo-800 dark:text-indigo-300 space-y-1">
                  <li>✓ {t('epassport.privacyBullet1')}</li>
                  <li>✓ {t('epassport.privacyBullet2')}</li>
                  <li>✓ {t('epassport.privacyBullet3')}</li>
                  <li>✓ {t('epassport.privacyBullet4')}</li>
                  <li>✓ {t('epassport.privacyBullet5')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Travel Readiness Check View
    if (currentView === 'travel-check') {
      const selectedCountryData = countries.find(
        (c) => c.code === selectedCountry
      );
      const groupedCountries = countries.reduce(
        (acc, country) => {
          if (!acc[country.region]) acc[country.region] = [];
          acc[country.region].push(country);
          return acc;
        },
        {} as Record<string, CountryRequirement[]>
      );

      return (
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => setCurrentView('main')}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              {t('epassport.backToDashboard')}
            </button>

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl">
              <div className="flex items-center gap-3">
                <Globe className="w-8 h-8" />
                <div>
                  <h2 className="text-2xl mb-1">
                    {t('epassport.travelReadinessCheck')}
                  </h2>
                  <p className="text-sm opacity-90">
                    {t('epassport.verifyComplianceDestination')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-6 mb-6 border dark:border-white/8">
            <label className="block text-sm text-gray-700 dark:text-white/70 mb-2">
              {t('epassport.selectDestinationCountry')}
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-[#0F0E16] dark:text-white"
            >
              <option value="">{t('epassport.chooseCountry')}</option>
              {Object.entries(groupedCountries).map(
                ([region, regionCountries]) => (
                  <optgroup
                    key={region}
                    label={t(
                      `travelCheck.region.${getTravelCheckRegionKey(region)}`
                    )}
                  >
                    {regionCountries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {getTravelCheckCountryName(
                          t,
                          country.code,
                          country.name
                        )}
                      </option>
                    ))}
                  </optgroup>
                )
              )}
            </select>
          </div>

          {selectedCountryData && (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-3xl">🌍</div>
                  <div>
                    <h3 className="text-xl text-blue-900 dark:text-blue-300">
                      {getTravelCheckCountryName(
                        t,
                        selectedCountryData.code,
                        selectedCountryData.name
                      )}
                    </h3>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      {t(
                        `travelCheck.region.${getTravelCheckRegionKey(selectedCountryData.region)}`
                      )}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {selectedCountryData.requirements.map((req, idx) => {
                    const validated = validateRequirement(req.name);
                    const status = validated.status;
                    return (
                      <div
                        key={idx}
                        className={`flex items-start gap-3 rounded-lg p-4 ${
                          status === 'compliant'
                            ? 'bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-700/30'
                            : status === 'action-required'
                              ? 'bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-700/30'
                              : 'bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-700/30'
                        }`}
                      >
                        {status === 'compliant' && (
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        )}
                        {status === 'action-required' && (
                          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        )}
                        {status === 'not-compliant' && (
                          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-900 dark:text-white font-semibold">
                              {TRAVEL_CHECK_REQ_KEYS[req.name]
                                ? t(
                                    `travelCheck.req.${TRAVEL_CHECK_REQ_KEYS[req.name]}`
                                  )
                                : req.name}
                            </div>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                status === 'compliant'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-800/30 dark:text-green-300'
                                  : status === 'action-required'
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-800/30 dark:text-amber-300'
                                    : 'bg-red-100 text-red-700 dark:bg-red-800/30 dark:text-red-300'
                              }`}
                            >
                              {status === 'compliant'
                                ? t('epassport.verified', 'Verified')
                                : status === 'action-required'
                                  ? t('epassport.actionRequired', 'Action Required')
                                  : t('epassport.notCompliant', 'Not Compliant')}
                            </span>
                          </div>
                          <div
                            className={`text-xs mt-1 ${
                              status === 'compliant'
                                ? 'text-green-600 dark:text-green-400'
                                : status === 'action-required'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-red-600 dark:text-red-400'
                            }`}
                          >
                            {TRAVEL_CHECK_DETAIL_KEYS[req.details]
                              ? t(
                                  `travelCheck.details.${TRAVEL_CHECK_DETAIL_KEYS[req.details]}`
                                )
                              : req.details}
                          </div>
                          {status === 'compliant' && validated.proof && (
                            <button
                              onClick={() => {
                                const proof = validated.proof;
                                if (proof.type === 'vaccination' && proof.record) {
                                  toast.info(`${proof.record.vaccineName || 'Vaccination'} — administered ${formatDateSafe(proof.record.dateAdministered)}${proof.record.expiryDate ? `, expires ${formatDateSafe(proof.record.expiryDate)}` : ''}`);
                                } else if (proof.type === 'certificate' && proof.record) {
                                  toast.info(`Certificate: ${proof.record.certificateType || 'N/A'} — issued ${formatDateSafe(proof.record.issuedAt)}`);
                                } else if (proof.type === 'medical' && proof.record) {
                                  toast.info(`Medical record: ${proof.record.title || 'N/A'} — ${formatDateSafe(proof.record.date)}`);
                                } else if (proof.type === 'microchip') {
                                  toast.info(`Microchip ID: ${pet.microchipId || (pet as any).microchip?.chipNumber || 'Linked'}`);
                                }
                              }}
                              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 underline underline-offset-2"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              {t('epassport.viewProof', 'View Proof')}
                            </button>
                          )}
                          {status === 'compliant' && validated.proof && (validated.proof.type === 'certificate' || validated.proof.type === 'vaccination') && validated.proof.record && (
                            <button
                              onClick={() => {
                                const proof = validated.proof;
                                if (proof.type === 'certificate') {
                                  setViewingTravelCert(proof.record);
                                } else if (proof.type === 'vaccination' && proof.record.vaccineName) {
                                  const matchCert = petCertificates.find((c: any) =>
                                    c.certificateType?.toLowerCase().includes('rabies') || c.certificateType?.toLowerCase().includes('vaccin')
                                  );
                                  if (matchCert) setViewingTravelCert(matchCert);
                                  else toast.info('No certificate document found for this vaccination');
                                }
                              }}
                              className="mt-2 ml-3 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline underline-offset-2"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              View Certificate
                            </button>
                          )}
                          {status !== 'compliant' && !req.name.toLowerCase().includes('import') && !req.name.toLowerCase().includes('cdc') && !req.name.toLowerCase().includes('quarantine') && (
                            <button
                              onClick={() => setShowTravelBooking(true)}
                              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                              style={{ backgroundColor: '#3B82F6', color: '#fff' }}
                            >
                              <Calendar className="w-3.5 h-3.5" />
                              Book Appointment
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700/30 rounded-lg p-4">
                <h4 className="text-orange-900 dark:text-orange-300 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {t('epassport.importantNotes')}
                </h4>
                <ul className="text-sm text-orange-800 dark:text-orange-300 space-y-1">
                  {selectedCountryData.notes.map((note, idx) => (
                    <li key={idx}>
                      •{' '}
                      {TRAVEL_CHECK_NOTE_KEYS[note]
                        ? t(`travelCheck.notes.${TRAVEL_CHECK_NOTE_KEYS[note]}`)
                        : note}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {!selectedCountryData && (
            <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-12 text-center">
              <Globe className="w-16 h-16 text-gray-400 dark:text-white/40 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-white/70">
                {t('epassport.selectDestinationToView')}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Share E-Passport View
    if (currentView === 'share') {
      return (
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => setCurrentView('main')}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              {t('epassport.backToDashboard')}
            </button>

            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-700 dark:to-indigo-700 text-white p-6 rounded-xl">
              <div className="flex items-center gap-3">
                <Share2 className="w-8 h-8" />
                <div>
                  <h2
                    className="text-2xl mb-1"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('epassport.shareEpassportTitle')}
                  </h2>
                  <p
                    className="text-sm opacity-90"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('epassport.shareEpassportSubtitle', { name: pet.name })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md border dark:border-white/8 p-6">
            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('epassport.shareWith')}
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  value={shareSettings.recipient}
                  onChange={(e) =>
                    setShareSettings({
                      ...shareSettings,
                      recipient: e.target.value,
                    })
                  }
                >
                  <option value="">{t('epassport.selectRecipientType')}</option>
                  <option value="airline">
                    {t('epassport.recipientAirline')}
                  </option>
                  <option value="vet">{t('epassport.recipientVet')}</option>
                  <option value="border">
                    {t('epassport.recipientBorder')}
                  </option>
                  <option value="hotel">{t('epassport.recipientHotel')}</option>
                  <option value="relocation">
                    {t('epassport.recipientRelocation')}
                  </option>
                </select>
              </div>

              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('epassport.sectionsToShare')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="full"
                      checked={shareSettings.scope === 'full'}
                      onChange={(e) =>
                        setShareSettings({
                          ...shareSettings,
                          scope: e.target.value,
                        })
                      }
                      className="w-4 h-4 text-purple-600 dark:text-purple-500"
                    />
                    <span
                      className="text-sm text-gray-900 dark:text-white/90"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('epassport.fullEpassport')}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 p-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="selected"
                      checked={shareSettings.scope === 'selected'}
                      onChange={(e) =>
                        setShareSettings({
                          ...shareSettings,
                          scope: e.target.value,
                        })
                      }
                      className="w-4 h-4 text-purple-600 dark:text-purple-500"
                    />
                    <span
                      className="text-sm text-gray-900 dark:text-white/90"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('epassport.selectedSectionsOnly')}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 p-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="zkp"
                      checked={shareSettings.scope === 'zkp'}
                      onChange={(e) =>
                        setShareSettings({
                          ...shareSettings,
                          scope: e.target.value,
                        })
                      }
                      className="w-4 h-4 text-purple-600 dark:text-purple-500"
                    />
                    <span
                      className="text-sm text-gray-900 dark:text-white/90"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('epassport.zkpVerificationOnly')}
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('epassport.accessDuration')}
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  value={shareSettings.duration}
                  onChange={(e) =>
                    setShareSettings({
                      ...shareSettings,
                      duration: e.target.value,
                    })
                  }
                >
                  <option value="24h">{t('epassport.duration24h')}</option>
                  <option value="7d">{t('epassport.duration7d')}</option>
                  <option value="30d">{t('epassport.duration30d')}</option>
                  <option value="manual">
                    {t('epassport.untilManuallyRevoked')}
                  </option>
                </select>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
                <p
                  className="text-sm text-yellow-800 dark:text-yellow-200"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  🔒 {t('epassport.accessRevokeNote')}
                </p>
              </div>

              <button
                onClick={handleGenerateShareLink}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-700 dark:to-indigo-700 text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                <Link className="w-5 h-5 inline mr-2" />
                {t('epassport.generateShareLink')}
              </button>

              {shareLink && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-sm text-green-900 dark:text-green-100 font-semibold"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('epassport.shareLinkGenerated')}
                    </span>
                    <button
                      onClick={handleCopyShareLink}
                      className="flex items-center gap-1 text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Copy className="w-4 h-4" />
                      {t('epassport.copy')}
                    </button>
                  </div>
                  <div className="bg-white dark:bg-[#0F0E16] border dark:border-white/20 rounded p-2 font-mono text-xs break-all text-gray-700 dark:text-white/90">
                    {shareLink}
                  </div>
                  <p
                    className="text-xs text-green-700 dark:text-green-300 mt-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('epassport.validFor')}{' '}
                    {shareSettings.duration === '24h'
                      ? t('epassport.duration24h')
                      : shareSettings.duration === '7d'
                        ? t('epassport.duration7d')
                        : shareSettings.duration === '30d'
                          ? t('epassport.duration30d')
                          : t('epassport.untilManuallyRevoked')}
                  </p>

                  <button
                    onClick={() => setShowShareOptionsModal(true)}
                    className="mt-4 w-full px-4 py-2 bg-white dark:bg-[#16151D] border border-green-300 dark:border-green-500/50 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors flex items-center justify-center gap-2"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    <Share2 className="w-4 h-4" />
                    {t('epassport.shareOptions')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // QR View
    if (currentView === 'qr-view') {
      return (
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => setCurrentView('main')}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              {t('epassport.backToDashboard')}
            </button>
          </div>

          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md border dark:border-white/8 p-6">
            <h2
              className="text-2xl mb-6 text-center text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
            >
              {t('epassport.qrCodeTitle')}
            </h2>

            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl p-8 mb-6">
              <div className="bg-white dark:bg-[#0F0E16] border dark:border-white/8 rounded-lg p-8 max-w-sm mx-auto">
                <QRCodeCanvas
                  value={qrContent}
                  size={320}
                  className="w-full h-auto max-w-[320px] mx-auto"
                />
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-[#0F0E16] border dark:border-white/8 rounded-lg p-4 mb-6">
              <div
                className="text-sm text-gray-600 dark:text-white/70 mb-2"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
              >
                {t('epassport.qrContains')}
              </div>
              <ul
                className="text-sm text-gray-800 dark:text-white/90 space-y-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <li>• {t('epassport.qrBullet1')}</li>
                <li>• {t('epassport.qrBullet2')}</li>
                <li>• {t('epassport.qrBullet3')}</li>
                <li>• {t('epassport.qrBullet4')}</li>
              </ul>
              <p
                className="text-xs text-gray-500 dark:text-white/50 mt-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('epassport.qrRawDocsPrivacyNote')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleDownloadQR}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                <Download className="w-5 h-5" />
                {t('epassport.downloadQr')}
              </button>
              <button
                onClick={handlePrintQR}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                <Printer className="w-5 h-5" />
                {t('epassport.printButton')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Section Detail View - Itemized Breakdown
    if (currentView === 'section-detail') {
      const section = sections.find((s) => s.id === selectedSection);

      // Define itemized data for each section
      const getSectionData = (sectionId: string) => {
        const docs = sectionDocuments[sectionId] || [];
        const mappedDocs = docs.map((d) => ({
          ...d,
          status: 'uploaded',
        }));

        switch (sectionId) {
          case 'identification': {
            const hasValue = (v: unknown) =>
              v !== undefined && v !== null && String(v).trim() !== '';
            return {
              fields: [
                {
                  name: 'Pet Name',
                  nameKey: 'fieldPetName',
                  status: hasValue(pet.name) ? 'completed' : 'missing',
                  value: pet.name || null,
                  editable: false,
                },
                {
                  name: 'Species',
                  nameKey: 'fieldSpecies',
                  status: hasValue(pet.species) ? 'completed' : 'missing',
                  value: pet.species || null,
                  editable: false,
                },
                {
                  name: 'Breed',
                  nameKey: 'fieldBreed',
                  status: hasValue(pet.breed) ? 'completed' : 'missing',
                  value: pet.breed || null,
                  editable: false,
                },
                {
                  name: 'Date of Birth',
                  nameKey: 'fieldDateOfBirth',
                  status: hasValue(pet.dateOfBirth) ? 'completed' : 'missing',
                  value: pet.dateOfBirth
                    ? formatDateSafe(pet.dateOfBirth)
                    : null,
                  editable: false,
                },
                {
                  name: 'Gender',
                  nameKey: 'fieldGender',
                  status: hasValue(pet.gender) ? 'completed' : 'missing',
                  value: pet.gender || null,
                  editable: false,
                },
                {
                  name: 'Color/Markings',
                  nameKey: 'fieldColorMarkings',
                  status: hasValue(pet.color) ? 'completed' : 'missing',
                  value: pet.color || null,
                  editable: false,
                },
              ],
              documents: mappedDocs,
            };
          }

          case 'owner': {
            const hasValue = (v: unknown) =>
              v !== undefined && v !== null && String(v).trim() !== '';
            const ownerName =
              ownerProfile?.name ||
              pet.owner?.name ||
              epassportOverrides.owner_name ||
              '';
            const ownerEmail =
              ownerProfile?.email ||
              pet.owner?.email ||
              epassportOverrides.owner_contact_email ||
              '';
            const ownerPhone =
              ownerProfile?.phone ||
              epassportOverrides.owner_contact_phone ||
              '';
            const addrParts = [
              ownerProfile?.address?.street,
              ownerProfile?.address?.city || ownerProfile?.address?.customCity,
              ownerProfile?.address?.state ||
                ownerProfile?.address?.customState,
              ownerProfile?.address?.zipCode,
              ownerProfile?.address?.country ||
                ownerProfile?.address?.customCountry,
            ].filter(Boolean);
            const address =
              addrParts.length > 0
                ? addrParts.join(', ')
                : [pet.city, pet.country].filter(Boolean).join(', ');
            return {
              fields: [
                {
                  name: 'Owner Name',
                  nameKey: 'fieldOwnerNameLabel',
                  status: hasValue(ownerName) ? 'completed' : 'missing',
                  value: ownerName || null,
                  editable: true,
                },
                {
                  name: 'Contact Email',
                  nameKey: 'fieldContactEmail',
                  status: hasValue(ownerEmail) ? 'completed' : 'missing',
                  value: ownerEmail || null,
                  editable: true,
                },
                {
                  name: 'Contact Phone',
                  nameKey: 'fieldContactPhone',
                  status: hasValue(ownerPhone) ? 'completed' : 'missing',
                  value: ownerPhone || null,
                  editable: true,
                },
                {
                  name: 'Address',
                  nameKey: 'fieldAddress',
                  status: hasValue(address) ? 'completed' : 'missing',
                  value: address || null,
                },
              ],
              documents: mappedDocs,
            };
          }

          case 'microchip': {
            const hasValue = (v: unknown) =>
              v !== undefined && v !== null && String(v).trim() !== '';
            const chipNumber =
              microchipData?.chipNumber || pet.microchipId || null;
            const standard = microchipData?.standard || null;
            const implantDate = microchipData?.implantDate || null;
            const implantLocation = microchipData?.implantLocation || null;
            return {
              fields: [
                {
                  name: 'Microchip Number',
                  nameKey: 'fieldMicrochipNumber',
                  status: hasValue(chipNumber) ? 'completed' : 'missing',
                  value: chipNumber,
                  editable: false,
                },
                {
                  name: 'Microchip Standard',
                  nameKey: 'fieldMicrochipStandard',
                  status: hasValue(standard) ? 'completed' : 'missing',
                  value: standard,
                  editable: false,
                },
                {
                  name: 'Implant Date',
                  nameKey: 'fieldImplantDate',
                  status: hasValue(implantDate) ? 'completed' : 'missing',
                  value: implantDate,
                  editable: false,
                },
                {
                  name: 'Implant Location',
                  nameKey: 'fieldImplantLocation',
                  status: hasValue(implantLocation) ? 'completed' : 'missing',
                  value: implantLocation,
                  editable: false,
                },
              ],
              documents: mappedDocs,
            };
          }

          case 'vaccinations':
            return { fields: [], documents: mappedDocs };

          case 'health-certificates':
            return { fields: [], documents: mappedDocs };

          case 'travel':
            return { fields: [], documents: mappedDocs };

          default:
            return { fields: [], documents: [] };
        }
      };

      const sectionData = getSectionData(selectedSection || '');

      // Calculate completion
      const completedFields = sectionData.fields.filter(
        (f: { status?: string }) => f.status === 'completed'
      ).length;
      const totalFields = sectionData.fields.length;
      const uploadedDocs = sectionData.documents.filter(
        (d: { status?: string }) => d.status === 'uploaded'
      ).length;
      const totalDocs = sectionData.documents.length;
      const completionPercent =
        selectedSection === 'vaccinations'
          ? vaccinations.length > 0
            ? 100
            : 0
          : selectedSection === 'health-certificates'
            ? medicalUpdates.length > 0
              ? 100
              : 0
            : totalFields > 0
              ? Math.round((completedFields / totalFields) * 100)
              : 0;

      return (
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => setCurrentView('main')}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              {t('epassport.backToDashboard')}
            </button>
          </div>

          {/* Section Header with Progress - mobile: padding, stack status row so "documents uploaded" not cut off */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-xl mb-6 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="text-3xl sm:text-4xl flex-shrink-0">
                  {section?.icon}
                </span>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl mb-1 truncate">
                    {section?.title}
                  </h2>
                  <p className="text-sm opacity-90">
                    {t('epassport.detailedSectionBreakdown')}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-2xl sm:text-3xl font-bold">
                  {completionPercent}%
                </div>
                <div className="text-xs opacity-75">
                  {t('epassport.complete')}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-white/20 rounded-full h-2 min-w-0">
              <div
                className="bg-white rounded-full h-2 transition-all"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
            <div className="flex flex-col gap-1 mt-2 text-xs opacity-90 sm:flex-row sm:items-center sm:justify-between">
              <span className="min-w-0 break-words">
                {selectedSection === 'vaccinations'
                  ? t('epassport.vaccinationsRecorded', {
                      count: vaccinations.length,
                    })
                  : selectedSection === 'health-certificates'
                    ? t('epassport.medicalUpdatesRecorded', {
                        count: medicalUpdates.length,
                      })
                    : selectedSection === 'travel'
                      ? t('epassport.travelReadinessCheck')
                      : t('epassport.fieldsCompleted', {
                          completed: completedFields,
                          total: totalFields,
                        })}
              </span>
              <span className="min-w-0 break-words sm:text-right">
                {t('epassport.documentsUploaded', {
                  uploaded: uploadedDocs,
                  total: totalDocs,
                })}
              </span>
            </div>
          </div>

          {/* Data Fields / Records Section */}
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 sm:p-6 mb-6 border dark:border-white/8">
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-xl dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {selectedSection === 'vaccinations'
                  ? t('epassport.records')
                  : selectedSection === 'health-certificates'
                    ? t('epassport.records')
                    : selectedSection === 'travel'
                      ? t('epassport.travelReadinessCheck')
                      : t('epassport.dataFields')}
              </h3>

              {/* Conditional action buttons */}
              {selectedSection === 'vaccinations' && (
                <button
                  onClick={() => {
                    setVaccinationInitialVaccine('');
                    setShowAddVaccinationModal(true);
                  }}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  {t('epassport.addNewRecord')}
                </button>
              )}

              {selectedSection === 'health-certificates' && (
                <button
                  onClick={() => setShowAddMedicalUpdateModal(true)}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  {t('epassport.addNewDetails')}
                </button>
              )}

              {selectedSection === 'microchip' && (
                <button
                  onClick={() =>
                    microchipData
                      ? setShowMicrochipManagementModal(true)
                      : setShowMicrochipLinkingModal(true)
                  }
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Link className="w-4 h-4" />
                  {microchipData
                    ? t('epassport.manageMicrochip')
                    : t('epassport.linkMicrochip')}
                </button>
              )}
            </div>

            <div className="space-y-3">
              {selectedSection === 'vaccinations' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-white/70 mb-3">
                    {t('epassport.vaccinationRecordsShared')}
                  </p>
                  {vaccinations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-white/50 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-lg">
                      <p className="text-sm mb-2">
                        {t('epassport.noVaccinationRecords')}
                      </p>
                      <button
                        onClick={() => {
                          setVaccinationInitialVaccine('');
                          setShowAddVaccinationModal(true);
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                      >
                        {t('epassport.addNewRecord')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {vaccinations.map((v) => (
                        <div
                          key={v.id}
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg"
                        >
                          <div>
                            <div className="font-medium dark:text-white">
                              {v.vaccineName}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-white/70 mt-1">
                              {t('epassport.administered')}{' '}
                              {new Date(
                                v.dateAdministered
                              ).toLocaleDateString()}{' '}
                              • {t('epassport.validUntil')}{' '}
                              {new Date(v.expiryDate).toLocaleDateString()}
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                            ✓ {t('epassport.recorded')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {selectedSection === 'health-certificates' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-white/70 mb-3">
                    {t('epassport.medicalUpdatesShared')}
                  </p>
                  {medicalUpdates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-white/50 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-lg">
                      <p className="text-sm mb-2">
                        {t('epassport.noMedicalUpdates')}
                      </p>
                      <button
                        onClick={() => setShowAddMedicalUpdateModal(true)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                      >
                        {t('epassport.addNewRecord')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {medicalUpdates.map((m) => (
                        <div
                          key={m.id}
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg"
                        >
                          <div>
                            <div className="font-medium dark:text-white">
                              {m.title} ({m.updateType})
                            </div>
                            <div className="text-xs text-gray-600 dark:text-white/70 mt-1">
                              {new Date(m.date).toLocaleDateString()} •{' '}
                              {m.veterinarian}
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                            ✓ {t('epassport.recorded')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {selectedSection === 'travel' &&
                (() => {
                  const selectedCountryData = countries.find(
                    (c) => c.code === selectedCountry
                  );
                  return (
                    <>
                      <p className="text-sm text-gray-600 dark:text-white/70 mb-3">
                        {t('epassport.selectDestinationDescription')}
                      </p>
                      <label className="block text-sm text-gray-700 dark:text-white/70 mb-2">
                        {t('epassport.selectDestinationCountryLabel')}
                      </label>
                      <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-white/8 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-[#0F0E16] dark:text-white mb-4"
                      >
                        <option value="">{t('epassport.chooseCountry')}</option>
                        {Object.entries(
                          countries.reduce(
                            (acc, c) => {
                              if (!acc[c.region]) acc[c.region] = [];
                              acc[c.region].push(c);
                              return acc;
                            },
                            {} as Record<string, typeof countries>
                          )
                        ).map(([region, regionCountries]) => (
                          <optgroup
                            key={region}
                            label={t(
                              `travelCheck.region.${getTravelCheckRegionKey(region)}`
                            )}
                          >
                            {regionCountries.map((c) => (
                              <option key={c.code} value={c.code}>
                                {getTravelCheckCountryName(t, c.code, c.name)}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      {selectedCountryData ? (
                        <div className="space-y-4">
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-xl p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="text-3xl">🌍</div>
                              <div>
                                <h3 className="text-xl text-blue-900 dark:text-blue-300">
                                  {getTravelCheckCountryName(
                                    t,
                                    selectedCountryData.code,
                                    selectedCountryData.name
                                  )}
                                </h3>
                                <p className="text-sm text-blue-700 dark:text-blue-400">
                                  {t(
                                    `travelCheck.region.${getTravelCheckRegionKey(selectedCountryData.region)}`
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-3">
                              {selectedCountryData.requirements.map(
                                (req, idx) => {
                                  const validated = validateRequirement(req.name);
                                  const status = validated.status;
                                  return (
                                    <div
                                      key={idx}
                                      className={`flex items-start gap-3 rounded-lg p-4 ${
                                        status === 'compliant'
                                          ? 'bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-700/30'
                                          : status === 'action-required'
                                            ? 'bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-700/30'
                                            : 'bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-700/30'
                                      }`}
                                    >
                                      {status === 'compliant' && (
                                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                      )}
                                      {status === 'action-required' && (
                                        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                                      )}
                                      {status === 'not-compliant' && (
                                        <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                                      )}
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {TRAVEL_CHECK_REQ_KEYS[req.name]
                                              ? t(
                                                  `travelCheck.req.${TRAVEL_CHECK_REQ_KEYS[req.name]}`
                                                )
                                              : req.name}
                                          </div>
                                          <span
                                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                              status === 'compliant'
                                                ? 'bg-green-100 text-green-700 dark:bg-green-800/30 dark:text-green-300'
                                                : status === 'action-required'
                                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-800/30 dark:text-amber-300'
                                                  : 'bg-red-100 text-red-700 dark:bg-red-800/30 dark:text-red-300'
                                            }`}
                                          >
                                            {status === 'compliant'
                                              ? t('epassport.verified', 'Verified')
                                              : status === 'action-required'
                                                ? t('epassport.actionRequired', 'Action Required')
                                                : t('epassport.notCompliant', 'Not Compliant')}
                                          </span>
                                        </div>
                                        <div
                                          className={`text-xs mt-1 ${
                                            status === 'compliant'
                                              ? 'text-green-600 dark:text-green-400'
                                              : status === 'action-required'
                                                ? 'text-amber-600 dark:text-amber-400'
                                                : 'text-red-600 dark:text-red-400'
                                          }`}
                                        >
                                          {status === 'compliant'
                                            ? t('epassport.complete')
                                            : t(
                                                'epassport.addInRelevantSections'
                                              )}
                                        </div>
                                        {status === 'compliant' && validated.proof && (
                                          <button
                                            onClick={() => {
                                              const proof = validated.proof;
                                              if (proof.type === 'vaccination' && proof.record) {
                                                toast.info(`${proof.record.vaccineName || 'Vaccination'} — administered ${formatDateSafe(proof.record.dateAdministered)}${proof.record.expiryDate ? `, expires ${formatDateSafe(proof.record.expiryDate)}` : ''}`);
                                              } else if (proof.type === 'certificate' && proof.record) {
                                                toast.info(`Certificate: ${proof.record.certificateType || 'N/A'} — issued ${formatDateSafe(proof.record.issuedAt)}`);
                                              } else if (proof.type === 'medical' && proof.record) {
                                                toast.info(`Medical record: ${proof.record.title || 'N/A'} — ${formatDateSafe(proof.record.date)}`);
                                              } else if (proof.type === 'microchip') {
                                                toast.info(`Microchip ID: ${pet.microchipId || (pet as any).microchip?.chipNumber || 'Linked'}`);
                                              }
                                            }}
                                            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 underline underline-offset-2"
                                          >
                                            <Eye className="w-3.5 h-3.5" />
                                            {t('epassport.viewProof', 'View Proof')}
                                          </button>
                                        )}
                                        {status === 'compliant' && validated.proof && (validated.proof.type === 'certificate' || validated.proof.type === 'vaccination') && validated.proof.record && (
                                          <button
                                            onClick={() => {
                                              const p = validated.proof;
                                              if (p.type === 'certificate') {
                                                setViewingTravelCert(p.record);
                                              } else {
                                                const mc = petCertificates.find((c: any) => c.certificateType?.toLowerCase().includes('rabies') || c.certificateType?.toLowerCase().includes('vaccin'));
                                                if (mc) setViewingTravelCert(mc);
                                                else toast.info('No certificate document found');
                                              }
                                            }}
                                            className="mt-2 ml-3 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline underline-offset-2"
                                          >
                                            <FileText className="w-3.5 h-3.5" />
                                            View Certificate
                                          </button>
                                        )}
                                        {status !== 'compliant' && !req.name.toLowerCase().includes('import') && !req.name.toLowerCase().includes('cdc') && !req.name.toLowerCase().includes('quarantine') && (
                                          <button
                                            onClick={() => setShowTravelBooking(true)}
                                            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                                            style={{ backgroundColor: '#3B82F6', color: '#fff' }}
                                          >
                                            <Calendar className="w-3.5 h-3.5" />
                                            Book Appointment
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700/30 rounded-lg p-4">
                            <h4 className="text-orange-900 dark:text-orange-300 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-5 h-5" />
                              {t('epassport.importantNotes')}
                            </h4>
                            <ul className="text-sm text-orange-800 dark:text-orange-300 space-y-1">
                              {selectedCountryData.notes.map((note, idx) => (
                                <li key={idx}>
                                  •{' '}
                                  {TRAVEL_CHECK_NOTE_KEYS[note]
                                    ? t(
                                        `travelCheck.notes.${TRAVEL_CHECK_NOTE_KEYS[note]}`
                                      )
                                    : note}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 dark:text-white/50 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-lg">
                          <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">
                            {t('epassport.selectDestinationToView')}
                          </p>
                        </div>
                      )}

                      {/* Insurance Section - interconnected with Health & Care Records */}
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-white/10">
                        <InsuranceRecords petId={pet.id} embedded />
                      </div>
                    </>
                  );
                })()}

              {selectedSection === 'microchip' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                        {t('epassport.microchipSyncedTitle', {
                          defaultValue: 'Synced with Overview',
                        })}
                      </p>
                      <p className="text-sm text-blue-800 dark:text-blue-200 mt-0.5">
                        {t('epassport.microchipSyncedDesc', {
                          defaultValue:
                            'Microchip details are automatically pulled from your Overview tab. To add or update your microchip, go to Overview → Manage Microchip.',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {selectedSection !== 'vaccinations' &&
                selectedSection !== 'health-certificates' &&
                selectedSection !== 'travel' &&
                sectionData.fields.map(
                  (
                    field: {
                      status?: string;
                      label?: string;
                      value?: string;
                      editable?: boolean;
                      name?: string;
                    },
                    idx: number
                  ) => (
                    <div
                      key={idx}
                      className="flex flex-col gap-3 p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {field.status === 'completed' && (
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                        )}
                        {field.status === 'needs-review' && (
                          <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                        )}
                        {field.status === 'missing' && (
                          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium dark:text-white">
                            {(field as { nameKey?: string }).nameKey
                              ? t(
                                  `epassport.${(field as { nameKey: string }).nameKey}`
                                )
                              : field.name}
                          </div>
                          {field.value && (
                            <div className="text-xs text-gray-600 dark:text-white/70 mt-1 break-words">
                              {field.value}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:flex-shrink-0">
                        {field.status === 'completed' && (
                          <>
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                              ✓ {t('epassport.statusCompleted')}
                            </span>
                            {field.editable !== false && (
                              <button
                                onClick={() =>
                                  setEditFieldModal({
                                    isOpen: true,
                                    field,
                                    sectionId: selectedSection || undefined,
                                  })
                                }
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                        {field.status === 'needs-review' && (
                          <>
                            <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded text-xs">
                              ⚠️ {t('epassport.statusNeedsReview')}
                            </span>
                            <button
                              onClick={() =>
                                setEditFieldModal({
                                  isOpen: true,
                                  field: { ...field, name: field.name },
                                  sectionId: selectedSection || undefined,
                                })
                              }
                              className="w-full sm:w-auto px-3 py-2 sm:py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-xs"
                            >
                              {t('epassport.review')}
                            </button>
                          </>
                        )}
                        {field.status === 'missing' && (
                          <>
                            <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs">
                              ❌ {t('epassport.statusMissing')}
                            </span>
                            <button
                              onClick={() =>
                                setEditFieldModal({
                                  isOpen: true,
                                  field,
                                  sectionId: selectedSection || undefined,
                                })
                              }
                              className="w-full sm:w-auto px-3 py-2 sm:py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                            >
                              {t('epassport.addDetails')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )
                )}
            </div>
          </div>

          {/* Physical Metrics Card - Pet Identification only, interconnected with Health & Care Records */}
          {selectedSection === 'identification' && (
            <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 sm:p-6 mb-6 border dark:border-white/8">
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-xl dark:text-white flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  {t('epassport.physicalMetrics')}
                </h3>
                <button
                  onClick={() => setShowAddMetricModal(true)}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  {t('epassport.addNewRecord')}
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-white/70 mb-4">
                {t('epassport.syncedWithHealthCarePhysical')}
              </p>
              <div className="space-y-3">
                {(() => {
                  const weightMetrics = physicalMetrics
                    .filter((m) => m.type === 'weight')
                    .sort(
                      (a, b) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                  const heightMetrics = physicalMetrics.filter(
                    (m) => m.type === 'height'
                  );
                  const bodyConditionMetrics = physicalMetrics.filter(
                    (m) => m.type === 'bodyCondition'
                  );
                  const latestWeight = weightMetrics[0];
                  const latestHeight = heightMetrics[0];
                  const latestBodyCondition = bodyConditionMetrics[0];
                  const hasAny =
                    latestWeight || latestHeight || latestBodyCondition;
                  if (!hasAny) {
                    return (
                      <div className="text-center py-8 text-gray-500 dark:text-white/50 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-lg">
                        <p className="text-sm mb-2">
                          {t('epassport.noPhysicalMetricsYet')}
                        </p>
                        <button
                          onClick={() => setShowAddMetricModal(true)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                        >
                          {t('epassport.addNewRecord')}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div className="grid gap-3 sm:grid-cols-3">
                      {latestWeight && (
                        <div className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
                          <div className="text-xs text-gray-600 dark:text-white/70 mb-1">
                            {t('epassport.weightLabel')}
                          </div>
                          <div className="font-medium dark:text-white">
                            {latestWeight.value} {latestWeight.unit}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-white/50 mt-1">
                            {new Date(latestWeight.date).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      {latestHeight && (
                        <div className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
                          <div className="text-xs text-gray-600 dark:text-white/70 mb-1">
                            {t('epassport.heightLengthLabel')}
                          </div>
                          <div className="font-medium dark:text-white">
                            {latestHeight.value} {latestHeight.unit}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-white/50 mt-1">
                            {new Date(latestHeight.date).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      {latestBodyCondition && (
                        <div className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
                          <div className="text-xs text-gray-600 dark:text-white/70 mb-1">
                            {t('epassport.bodyConditionLabel')}
                          </div>
                          <div className="font-medium dark:text-white">
                            {latestBodyCondition.value}{' '}
                            {latestBodyCondition.unit}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-white/50 mt-1">
                            {new Date(
                              latestBodyCondition.date
                            ).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Uploaded Documents Section */}
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 sm:p-6 border dark:border-white/8">
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-xl dark:text-white flex items-center gap-2">
                <Upload className="w-5 h-5" />
                {t('epassport.documentsSection')}
              </h3>
              <button
                onClick={() =>
                  setDocumentUploadModal({
                    isOpen: true,
                    documentName: t('epassport.newDocumentLabel'),
                    isReplace: false,
                    documentUrl: undefined,
                  })
                }
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                <Plus className="w-4 h-4" />
                {t('epassport.uploadDocument')}
              </button>
            </div>

            <div className="space-y-3">
              {sectionData.documents.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-white/50">
                  <Upload className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {t('epassport.noDocumentsUploadedYet')}
                  </p>
                </div>
              )}

              {sectionData.documents.map(
                (doc: PassportDocument & { status: string }, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {doc.status === 'uploaded' && (
                        <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      )}
                      {doc.status === 'needs-review' && (
                        <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                      )}
                      {doc.status === 'missing' && (
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                      )}

                      <div className="flex-1">
                        <div className="text-sm font-medium dark:text-white">
                          {doc.name}
                        </div>
                        {doc.status !== 'missing' && doc.date && (
                          <div className="text-xs text-gray-600 dark:text-white/70 mt-1">
                            {t('epassportModal.uploadedBy', {
                              date: new Date(doc.date).toLocaleDateString(),
                              source: doc.source,
                            })}
                          </div>
                        )}
                        {doc.status === 'missing' && doc.required && (
                          <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                            ⚠️ {t('epassport.requiredDocument')}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {doc.status === 'uploaded' && (
                        <>
                          <button
                            onClick={() => {
                              handleDownload(doc);
                            }}
                            className="px-3 py-1 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded text-xs dark:text-white"
                          >
                            {t('epassport.download')}
                          </button>
                          <button
                            onClick={() =>
                              setDocumentViewModal({
                                isOpen: true,
                                document: doc,
                              })
                            }
                            className="px-3 py-1 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded text-xs dark:text-white"
                          >
                            {t('epassport.view')}
                          </button>
                          <button
                            onClick={() =>
                              setDocumentUploadModal({
                                isOpen: true,
                                documentName: doc.name,
                                isReplace: true,
                                documentUrl: doc.url,
                              })
                            }
                            className="px-3 py-1 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded text-xs dark:text-white"
                          >
                            {t('epassportModal.replace')}
                          </button>
                        </>
                      )}
                      {doc.status === 'needs-review' && (
                        <button
                          onClick={() =>
                            setReviewModal({
                              isOpen: true,
                              item: { ...doc, type: 'document' },
                            })
                          }
                          className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-xs"
                        >
                          {t('epassportModal.review')}
                        </button>
                      )}
                      {doc.status === 'missing' && (
                        <button
                          onClick={() =>
                            setDocumentUploadModal({
                              isOpen: true,
                              documentName: doc.name,
                              isReplace: false,
                              documentUrl: undefined,
                            })
                          }
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                        >
                          {t('epassport.uploadDocument')}
                        </button>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Admin Actions - stack buttons on mobile so none are cut off */}
          <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-700/30 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-2">
              {t('epassport.adminActions')}
            </h4>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <button
                onClick={() => toast(t('epassport.flagForReview'))}
                className="w-full sm:w-auto px-3 py-2 sm:py-1 border border-purple-300 dark:border-purple-700/30 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded text-xs text-purple-900 dark:text-purple-300"
              >
                {t('epassport.flagForReview')}
              </button>
              <button
                onClick={() => toast(t('epassport.requestUpdateFromOwner'))}
                className="w-full sm:w-auto px-3 py-2 sm:py-1 border border-purple-300 dark:border-purple-700/30 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded text-xs text-purple-900 dark:text-purple-300"
              >
                {t('epassport.requestUpdateFromOwner')}
              </button>
              <button
                onClick={() => toast(t('epassport.markSectionVerified'))}
                className="w-full sm:w-auto px-3 py-2 sm:py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs"
              >
                {t('epassport.markSectionVerified')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {renderContent()}

      {/* Modals */}
      <DocumentViewModal
        isOpen={documentViewModal.isOpen}
        onClose={() => setDocumentViewModal({ isOpen: false, document: null })}
        onDownload={handleDownload}
        document={
          documentViewModal.document || {
            name: '',
            date: '',
            source: '',
            type: '',
            url: '',
          }
        }
      />

      <DocumentUploadModal
        isOpen={documentUploadModal.isOpen}
        onClose={() =>
          setDocumentUploadModal({
            isOpen: false,
            documentName: '',
            isReplace: false,
          })
        }
        documentName={documentUploadModal.documentName}
        isReplace={documentUploadModal.isReplace}
        folder={selectedSection || 'passport'}
        onUploadComplete={(fileInfo: {
          name: string;
          type: string;
          size: string;
          url: string;
        }) => {
          const newDoc: PassportDocument = {
            name: fileInfo.name,
            type: fileInfo.type,
            date: new Date().toISOString(),
            size: fileInfo.size,
            source: 'Owner',
            url: fileInfo.url,
          };

          const targetFolder = selectedSection || 'passport';

          if (documentUploadModal.isReplace) {
            if (targetFolder === 'passport') {
              setPassportDocuments((prev) => {
                const updated = prev.map((doc) =>
                  doc.url === documentUploadModal.documentUrl ? newDoc : doc
                );
                try {
                  localStorage.setItem(
                    PASSPORT_DOCS_KEY,
                    JSON.stringify(updated)
                  );
                } catch {
                  /* ignore */
                }
                return updated;
              });
            }
            setSectionDocuments((prev) => ({
              ...prev,
              [targetFolder]: (prev[targetFolder] || []).map((doc) =>
                doc.url === documentUploadModal.documentUrl ? newDoc : doc
              ),
            }));
          } else {
            if (targetFolder === 'passport') {
              setPassportDocuments((prev) => {
                const updated = [...prev, newDoc];
                try {
                  localStorage.setItem(
                    PASSPORT_DOCS_KEY,
                    JSON.stringify(updated)
                  );
                } catch {
                  /* ignore */
                }
                return updated;
              });
            }
            setSectionDocuments((prev) => ({
              ...prev,
              [targetFolder]: [...(prev[targetFolder] || []), newDoc],
            }));
          }
          addNotification({
            type: 'document_shared',
            title: 'Document added',
            message: `"${fileInfo.name}" was added to ${pet.name}'s E-Passport.`,
            petId: pet.id,
            petName: pet.name,
            icon: '📄',
            source: 'E-Passport',
          });
        }}
      />

      <EditFieldModal
        isOpen={editFieldModal.isOpen}
        onClose={() =>
          setEditFieldModal({
            isOpen: false,
            field: null,
            sectionId: undefined,
          })
        }
        field={
          editFieldModal.field || {
            name: '',
            value: '',
            status: '',
          }
        }
        onSave={async (value) => {
          const fieldName = editFieldModal.field?.name;
          const sectionId = editFieldModal.sectionId;
          const trimmed = String(value ?? '').trim();
          if (sectionId === 'microchip' && microchipData && fieldName) {
            try {
              await updateMicrochipSettings(pet.id, {
                chipNumber:
                  fieldName === 'Microchip Number'
                    ? trimmed
                    : microchipData.chipNumber,
                standard:
                  fieldName === 'Microchip Standard'
                    ? (trimmed as 'ISO' | 'AVID' | 'Other')
                    : microchipData.standard,
                implantDate:
                  fieldName === 'Implant Date'
                    ? trimmed
                    : microchipData.implantDate,
                implantLocation:
                  fieldName === 'Implant Location'
                    ? trimmed
                    : microchipData.implantLocation,
                linkToDID: microchipData.linkToDID,
                visibility: microchipData.visibility,
              });
              setMicrochipData((prev) =>
                prev
                  ? {
                      ...prev,
                      ...(fieldName === 'Microchip Number' && {
                        chipNumber: trimmed,
                      }),
                      ...(fieldName === 'Microchip Standard' && {
                        standard: trimmed as 'ISO' | 'AVID' | 'Other',
                      }),
                      ...(fieldName === 'Implant Date' && {
                        implantDate: trimmed,
                      }),
                      ...(fieldName === 'Implant Location' && {
                        implantLocation: trimmed,
                      }),
                    }
                  : null
              );
              await refreshPets();
              window.dispatchEvent(
                new CustomEvent('microchip-updated', {
                  detail: { petId: pet.id },
                })
              );
              toast.success(t('epassport.microchipUpdated'));
            } catch (err) {
              console.error('Failed to update microchip:', err);
              toast.error('Failed to save. Please try again.');
            }
          } else if (sectionId === 'owner' && fieldName) {
            if (fieldName === 'Owner Name') {
              setOwnerProfile((prev) =>
                prev
                  ? { ...prev, name: trimmed }
                  : { name: trimmed, email: '', phone: '', address: {} }
              );
              await apiClient.put('/users/profile', { name: trimmed });
              setEpassportOverrides((prev) => ({
                ...prev,
                owner_name: trimmed,
              }));
            } else if (fieldName === 'Contact Phone') {
              setOwnerProfile((prev) =>
                prev
                  ? { ...prev, phone: trimmed }
                  : { name: '', email: '', phone: trimmed, address: {} }
              );
              await apiClient.put('/users/profile', { phone: trimmed });
              setEpassportOverrides((prev) => ({
                ...prev,
                owner_contact_phone: trimmed,
              }));
            } else if (fieldName === 'Contact Email') {
              // Email is managed by Clerk/auth — store locally only
              setEpassportOverrides((prev) => ({
                ...prev,
                owner_contact_email: trimmed,
              }));
            }
            toast.success(`Updated ${fieldName} successfully`);
          } else {
            toast.success(`Updated ${fieldName} successfully`);
          }
          setEditFieldModal({
            isOpen: false,
            field: null,
            sectionId: undefined,
          });
        }}
      />

      <ReviewModal
        isOpen={reviewModal.isOpen}
        onClose={() => setReviewModal({ isOpen: false, item: null })}
        item={reviewModal.item || { name: '', type: 'field' }}
        onApprove={() => {
          console.log('Approved');
          // Handle approve
        }}
        onReject={(reason) => {
          console.log('Rejected:', reason);
          // Handle reject
        }}
      />

      {showAddVaccinationModal && (
        <AddVaccinationModal
          onClose={() => setShowAddVaccinationModal(false)}
          initialVaccine={vaccinationInitialVaccine}
          onAdd={async (vaccinationData) => {
            try {
              await createVaccinationMutation.mutateAsync({
                vaccineName: vaccinationData.vaccine,
                dateAdministered: vaccinationData.dateAdministered,
                expiryDate: vaccinationData.expiryDate,
                boosterDate: vaccinationData.boosterDate,
                administeredBy: vaccinationData.administeredBy,
                clinicName: vaccinationData.clinicName,
                batchNumber: vaccinationData.batchNumber,
                verified: false,
                notes: vaccinationData.notes,
              });
              toast.success('Vaccination record added to E-Passport');
              setShowAddVaccinationModal(false);
            } catch (err) {
              console.error('Failed to add vaccination', err);
              toast.error('Failed to add vaccination. Please try again.');
            }
          }}
        />
      )}

      {showAddMedicalUpdateModal && (
        <AddMedicalUpdateModal
          onClose={() => setShowAddMedicalUpdateModal(false)}
          onAdd={async (updateData) => {
            try {
              await createMedicalUpdateMutation.mutateAsync({
                updateType: updateData.type,
                title: updateData.title,
                date: updateData.date,
                veterinarian: updateData.veterinarian,
                clinicName: updateData.clinicName,
                description: updateData.description,
                medications: updateData.medications,
                status: updateData.status,
                followUpDate: updateData.followUpDate,
              });
              toast.success('Medical update added to E-Passport');
              setShowAddMedicalUpdateModal(false);
            } catch (err) {
              console.error('Failed to add medical update', err);
              toast.error('Failed to add medical update. Please try again.');
            }
          }}
        />
      )}

      {showAddMetricModal && (
        <AddMetricModal
          onClose={() => setShowAddMetricModal(false)}
          onAdd={async (metricData) => {
            try {
              await createPhysicalMetricMutation.mutateAsync({
                type: metricData.type as MetricType,
                value: metricData.value,
                unit: metricData.unit,
                date: metricData.date,
                updatedBy: 'Current User',
                updatedByType: 'owner' as UpdaterType,
                verified: false,
                notes: metricData.notes,
              });
              toast.success('Physical metric added');
              setShowAddMetricModal(false);
            } catch (err) {
              console.error('Failed to add metric', err);
              toast.error('Failed to add metric. Please try again.');
            }
          }}
        />
      )}

      {showMicrochipLinkingModal && (
        <MicrochipLinking
          petId={pet.id}
          petName={pet.name}
          hasDID={!!pet.did}
          onComplete={async (chipData: MicrochipData) => {
            try {
              await linkMicrochip(pet.id, {
                chipNumber: chipData.chipNumber,
                standard: chipData.standard,
                implantDate: chipData.implantDate,
                implantLocation: chipData.implantLocation,
                linkToDID: chipData.linkToDID,
                visibility: chipData.visibility,
              });
              setMicrochipData({
                ...chipData,
                linkedDate: new Date().toISOString(),
              });
              window.dispatchEvent(
                new CustomEvent('microchip-updated', {
                  detail: { petId: pet.id },
                })
              );
              toast.success(t('epassport.microchipLinkedSync'));
              setShowMicrochipLinkingModal(false);
            } catch (error) {
              console.error('Failed to link microchip:', error);
              toast.error(t('epassport.failedToLinkMicrochip'));
            }
          }}
          onCancel={() => setShowMicrochipLinkingModal(false)}
        />
      )}

      {showMicrochipManagementModal && microchipData && (
        <MicrochipManagement
          petName={pet.name}
          chipData={microchipData}
          onUpdate={async (updates) => {
            try {
              await updateMicrochipSettings(pet.id, {
                chipNumber: microchipData.chipNumber,
                standard: updates.standard ?? microchipData.standard,
                implantDate: updates.implantDate ?? microchipData.implantDate,
                implantLocation:
                  updates.implantLocation ?? microchipData.implantLocation,
                linkToDID: updates.linkToDID ?? microchipData.linkToDID,
                visibility: updates.visibility ?? microchipData.visibility,
              });
              setMicrochipData({ ...microchipData, ...updates });
              window.dispatchEvent(
                new CustomEvent('microchip-updated', {
                  detail: { petId: pet.id },
                })
              );
            } catch (error) {
              console.error('Failed to update microchip:', error);
              toast.error('Failed to update. Please try again.');
            }
          }}
          onUnlink={async () => {
            try {
              await unlinkMicrochip(pet.id);
              setMicrochipData(null);
              window.dispatchEvent(
                new CustomEvent('microchip-updated', {
                  detail: { petId: pet.id },
                })
              );
              setShowMicrochipManagementModal(false);
              toast.success(t('epassport.microchipUnlinked'));
            } catch (error) {
              console.error('Failed to unlink microchip:', error);
              toast.error('Failed to unlink. Please try again.');
            }
          }}
          onClose={() => setShowMicrochipManagementModal(false)}
        />
      )}

      {showShareOptionsModal && (
        <ShareOptionsModal
          isOpen={showShareOptionsModal}
          onClose={() => setShowShareOptionsModal(false)}
          url={shareLink}
          itemType="E-Passport"
          itemName={`${pet.name}'s E-Passport`}
        />
      )}
      {/* Travel Check Booking Flow */}
      {showTravelBooking && (
        <BookingFlow onClose={() => setShowTravelBooking(false)} initialPet={pet} />
      )}

      {/* Travel Check Certificate Viewer Modal */}
      {viewingTravelCert && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={(e) => { if (e.target === e.currentTarget) setViewingTravelCert(null); }}>
          <div className="bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl" style={{ maxWidth: '28rem', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: 'linear-gradient(to right, #059669, #047857)', color: 'white', padding: '1.5rem', borderRadius: '1rem 1rem 0 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldCheck style={{ width: '20px', height: '20px' }} />
                  {viewingTravelCert.title || 'Certificate'}
                </h2>
                <button type="button" onClick={() => setViewingTravelCert(null)} style={{ color: 'rgba(255,255,255,0.8)', cursor: 'pointer', background: 'none', border: 'none' }}>
                  <XCircle style={{ width: '20px', height: '20px' }} />
                </button>
              </div>
              <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>{viewingTravelCert.certificateNumber}</p>
            </div>
            <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ width: '100%', background: '#fff', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                <div style={{ background: '#0f172a', padding: '16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase' }}>Issued by</p>
                    <p style={{ fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>{viewingTravelCert.issuedByTitle ? `${viewingTravelCert.issuedByTitle} ` : ''}{viewingTravelCert.issuedByName || 'Professional'}</p>
                    {viewingTravelCert.businessName && <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{viewingTravelCert.businessName}</p>}
                  </div>
                  <ShieldCheck style={{ color: '#34d399', width: '22px', height: '22px' }} />
                </div>
                <div style={{ background: '#f8fafc', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Issued To</p>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{viewingTravelCert.petName || pet.name}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Document</p>
                    <p style={{ fontSize: '11px', fontWeight: 500, color: '#0f172a' }}>{viewingTravelCert.title}</p>
                  </div>
                </div>
                {viewingTravelCert.data && (() => {
                  const d = typeof viewingTravelCert.data === 'string' ? (() => { try { return JSON.parse(viewingTravelCert.data); } catch { return {}; } })() : viewingTravelCert.data;
                  return (
                    <div style={{ padding: '12px 16px' }}>
                      {Object.entries(d).filter(([k, v]) => v && !['certificateId','certificateNumber','certificateType','title','issuedAt','issuedByName','issuedByTitle','documentHash','systems'].includes(k)).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', borderBottom: '1px solid #f1f5f9', padding: '4px 0' }}>
                          <span style={{ color: '#64748b', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}</span>
                          <span style={{ fontWeight: 500, color: '#1e293b' }}>{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', fontSize: '11px' }}>
                  {viewingTravelCert.certificateNumber && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Certificate No.</span><span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', fontSize: '10px' }}>{viewingTravelCert.certificateNumber}</span></div>}
                  {viewingTravelCert.issuedAt && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Issued</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{new Date(viewingTravelCert.issuedAt).toLocaleDateString()}</span></div>}
                  {viewingTravelCert.validFrom && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Valid From</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{viewingTravelCert.validFrom}</span></div>}
                  {viewingTravelCert.validUntil && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Valid Until</span><span style={{ fontWeight: 500, color: '#0f172a' }}>{viewingTravelCert.validUntil}</span></div>}
                </div>
                <div style={{ background: '#ecfdf5', padding: '12px 16px', borderTop: '1px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: '8px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '3px' }}><ShieldCheck style={{ width: '10px', height: '10px' }} /> Cryptographically Verified</p>
                    <p style={{ fontSize: '7px', color: '#047857', fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>{viewingTravelCert.documentHash ? String(viewingTravelCert.documentHash).slice(0, 32) + '...' : viewingTravelCert.certificateNumber}</p>
                  </div>
                  <div style={{ background: '#fff', padding: '4px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                    <QRCodeCanvas value={viewingTravelCert.verificationUrl || `https://api-dev.petso.dev/verify/${viewingTravelCert.certificateNumber}`} size={48} />
                  </div>
                </div>
              </div>
              {viewingTravelCert.verificationUrl && (
                <a href={viewingTravelCert.verificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mt-3">
                  <ExternalLink className="w-4 h-4" />
                  Verify certificate publicly
                </a>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
              <button type="button" onClick={() => setViewingTravelCert(null)} style={{ width: '100%', padding: '10px', backgroundColor: '#059669', color: '#fff', borderRadius: '8px', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
